import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { 
  MessageSquare, Send, Store, Utensils, Film, Clock, MapPin, Phone, 
  ChevronRight, ChevronLeft, X, CheckCircle, ArrowLeft, Calendar, Users, Ticket,
  Coffee, ShoppingBag, Check, CheckCheck, Search,
  Loader2, Sparkles, Glasses, CreditCard, QrCode, AlertCircle, Mail, Globe,
  Facebook, Instagram, Twitter, ExternalLink, User, RefreshCw, Crown,
  Wine, Beer, Droplets, IceCream2, Pizza, Sandwich, Cookie, GlassWater, LayoutGrid,
  ChevronDown, BriefcaseBusiness
} from "lucide-react";

const escapeHtml = (text) =>
  String(text)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const apiErrorMessage = (error, fallback) => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string" && detail.trim()) {
    return detail;
  }
  if (Array.isArray(detail)) {
    return detail.map((item) => item.msg || item.detail || JSON.stringify(item)).join("; ");
  }
  return fallback;
};

const linkifyText = (text) => {
  if (!text) return text;
  let result = escapeHtml(text);
  const patterns = [
    { regex: /(\+?\d{1,3}[-.\s]?)?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}/g, type: 'phone' },
    { regex: /([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9_-]+)/g, type: 'email' },
    { regex: /(https?:\/\/[^\s]+)/g, type: 'url' },
    { regex: /(www\.[^\s]+)/g, type: 'www' }
  ];
  patterns.forEach(({ regex, type }) => {
    result = result.replace(regex, (match) => {
      if (type === 'phone') {
        return `<a href="tel:${match}" style="color: #2563eb; text-decoration: underline; cursor: pointer;">${match}</a>`;
      }
      if (type === 'email') {
        return `<a href="mailto:${match}" style="color: #2563eb; text-decoration: underline; cursor: pointer;">${match}</a>`;
      }
      if (type === 'url') {
        return `<a href="${match}" target="_blank" rel="noopener noreferrer" style="color: #2563eb; text-decoration: underline; cursor: pointer;">${match}</a>`;
      }
      return `<a href="https://${match}" target="_blank" rel="noopener noreferrer" style="color: #2563eb; text-decoration: underline; cursor: pointer;">${match}</a>`;
    });
  });
  return result;
};

const API_URL = process.env.REACT_APP_BACKEND_URL;
const IDLE_LIMIT_MS = 3 * 60 * 1000;
const IDLE_NOTICE_SECONDS = 10;

const isChatEmbed = () => {
  const params = new URLSearchParams(window.location.search);
  return params.get("embed") === "true" || window.self !== window.top;
};

const kenyanMobileDigits = (phone) => String(phone || "").replace(/\D/g, "");

/** National significant number for +254 field (9 digits: 7xxxxxxxx / 1xxxxxxxx). */
const sanitizeKenyanPhoneInput = (raw) => {
  let digits = kenyanMobileDigits(raw);
  // Strip pasted country / trunk prefixes so "+254" UI prefix is never duplicated.
  while (digits.startsWith("254") && digits.length > 9) {
    digits = digits.slice(3);
  }
  while (digits.startsWith("0") && digits.length > 9) {
    digits = digits.slice(1);
  }
  if (digits.startsWith("0")) {
    digits = digits.slice(1);
  }
  return digits.slice(0, 9);
};

const toKenyanApiPhone = (phone) => {
  const digits = kenyanMobileDigits(phone);
  if (!digits) return "";
  if (/^254[17]\d{8}$/.test(digits)) return digits;
  if (/^0[17]\d{8}$/.test(digits)) return `254${digits.slice(1)}`;
  if (/^[17]\d{8}$/.test(digits)) return `254${digits}`;
  const nsn = sanitizeKenyanPhoneInput(digits);
  if (/^[17]\d{8}$/.test(nsn)) return `254${nsn}`;
  return digits;
};

const isKenyanMobileNumber = (phone) => {
  const api = toKenyanApiPhone(phone);
  return /^254[17]\d{8}$/.test(api);
};

const formatPhoneForPayment = (phone) => {
  const api = toKenyanApiPhone(phone);
  if (/^254[17]\d{8}$/.test(api)) {
    return `0${api.slice(3)}`;
  }
  const digits = kenyanMobileDigits(phone);
  if (/^0[17]\d{8}$/.test(digits)) return digits;
  if (/^[17]\d{8}$/.test(digits)) return `0${digits}`;
  return digits;
};

const visitorPhoneNsn = (phone) => {
  const api = toKenyanApiPhone(phone);
  if (/^254[17]\d{8}$/.test(api)) {
    return api.slice(3);
  }
  return sanitizeKenyanPhoneInput(phone);
};

/** Normalize messy CMS floor strings for chips / filters. */
const normalizeFloorLabel = (raw) => {
  const cleaned = String(raw || "").replace(/\s+/g, " ").trim();
  if (!cleaned) return "Other";
  const compact = cleaned.toLowerCase().replace(/[\s_-]+/g, "");
  if (compact.includes("basement")) return "Basement";
  if (compact.includes("ground") || compact === "g" || compact === "gf") return "Ground Floor";
  if (compact.includes("first") || compact === "1" || compact === "1st") return "First Floor";
  if (compact.includes("second") || compact === "2" || compact === "2nd") return "Second Floor";
  if (compact.includes("third") || compact.includes("thrid") || compact === "3" || compact === "3rd") {
    return "Third Floor";
  }
  if (compact.includes("popup") || compact.includes("pop-up") || cleaned.toLowerCase().includes("pop up")) {
    return "Pop-Up Market";
  }
  return cleaned;
};

const floorSortKey = (label) => {
  const n = normalizeFloorLabel(label).toLowerCase();
  if (n.includes("basement")) return 0;
  if (n.includes("ground")) return 1;
  if (n.includes("first")) return 2;
  if (n.includes("second")) return 3;
  if (n.includes("third")) return 4;
  if (n.includes("pop")) return 5;
  if (n === "other") return 99;
  return 50;
};

const collectFloorOptions = (items) => {
  const counts = new Map();
  for (const item of items || []) {
    const label = normalizeFloorLabel(item?.floor);
    counts.set(label, (counts.get(label) || 0) + 1);
  }
  return [...counts.entries()]
    .sort((a, b) => floorSortKey(a[0]) - floorSortKey(b[0]) || a[0].localeCompare(b[0]))
    .map(([label, count]) => ({ label, count }));
};

const westgateMapsUrl = (item) => {
  const name = String(item?.name || "").trim();
  const floor = normalizeFloorLabel(item?.floor);
  const unit = item?.unit_number ? `, Unit ${item.unit_number}` : "";
  const floorBit = floor !== "Other" ? ` (${floor}${unit})` : unit ? ` (${unit.replace(/^, /, "")})` : "";
  const query = name
    ? `${name}${floorBit}, Westgate Shopping Mall, Nairobi`
    : "Westgate Shopping Mall, Mwanzi Road, Nairobi";
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
};

const DirectoryFloorChips = ({ items, selectedFloor, onSelect }) => {
  const floors = collectFloorOptions(items);
  if (floors.length <= 1) return null;
  const chips = [{ label: "All floors", value: "all", count: items.length }, ...floors.map((f) => ({
    label: f.label,
    value: f.label,
    count: f.count,
  }))];
  return (
    <div
      className="-mx-3 px-3 pt-2 overflow-x-auto"
      role="tablist"
      aria-label="Filter by floor"
      data-testid="directory-floor-chips"
    >
      <div className="flex gap-2 pb-1 min-w-max">
        {chips.map((chip) => {
          const active = selectedFloor === chip.value;
          return (
            <button
              key={chip.value}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => onSelect(chip.value)}
              className={`shrink-0 min-h-11 px-3 py-2 rounded-lg border text-xs font-semibold transition-colors ${
                active
                  ? "bg-[#EC1A23] border-[#EC1A23] text-white"
                  : "bg-[var(--cb-chip)] border-[var(--cb-chip-border)] text-[var(--cb-ink-secondary)] hover:border-[#EC1A23]"
              }`}
            >
              {chip.label}
              <span className={`ml-1.5 tabular-nums ${active ? "text-white/80" : "text-[var(--cb-ink-muted)]"}`}>
                {chip.count}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

/** Split "06:50 PM (Screen 1)" into time + screen for typography hierarchy. */
const parseShowtimeLabel = (label) => {
  const text = String(label || "").trim();
  const match = text.match(/^(.+?)\s*\((.+)\)\s*$/);
  if (!match) {
    return { time: text, screen: "" };
  }
  return { time: match[1].trim(), screen: match[2].trim() };
};

const getConcessionCategoryIcon = (categoryName) => {
  const name = String(categoryName || "").toLowerCase();
  if (!name || name === "all") return LayoutGrid;
  if (name.includes("espresso") || name.includes("coffee") || name.includes("tea") || name.includes("hot chocolate")) return Coffee;
  if (name.includes("shake") || name.includes("slush") || name.includes("ice cream") || name.includes("donut")) return IceCream2;
  if (name.includes("juice") || name.includes("lemonade") || name.includes("soda") || name.includes("mocktail")) return GlassWater;
  if (name.includes("water")) return Droplets;
  if (name.includes("wine") || name.includes("cocktail") || name.includes("alcoholic")) return Wine;
  if (name.includes("beer") || name.includes("cider") || name.includes("alcopop")) return Beer;
  if (name.includes("pizza")) return Pizza;
  if (name.includes("sandwich") || name.includes("slider") || name.includes("wrap") || name.includes("hot dog")) return Sandwich;
  if (name.includes("cookie") || name.includes("croissant") || name.includes("nacho")) return Cookie;
  return Utensils;
};

// Simple UUID v4 generator
const generateUUID = () => {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === "x" ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};


// Quick action buttons — Movies / Food / Stores / Hours first; Services last
const QUICK_ACTIONS = [
  { id: "movies", label: "Movies", icon: Film },
  { id: "restaurants", label: "Food", icon: Utensils },
  { id: "stores", label: "Stores", icon: Store },
  { id: "hours", label: "Hours", icon: Clock },
  { id: "services", label: "Services", icon: BriefcaseBusiness },
];

const POLICY_LINKS = {
  terms: "https://www.westgate.co.ke/terms",
  privacy: "https://www.westgate.co.ke/privacy",
  cookies: "https://www.westgate.co.ke/cookies",
};

const SAMPLE_QUESTIONS = [
  "Where can I find Nike?",
  "What's showing at the cinema tonight?",
  "Bollywood movies this weekend?",
  "best movie to watch this weekend?",
  "Mall opening hours today?",
  "Restaurants on the ground floor?",
  "How do I book a table?",
  "Where is the cinema?",
  "Any kids activities this weekend?",
  "Parking information?",
  "Stores that sell electronics?",
];

const pickSampleQuestion = (exclude) => {
  const pool = SAMPLE_QUESTIONS.filter((item) => item !== exclude);
  const choices = pool.length ? pool : SAMPLE_QUESTIONS;
  return choices[Math.floor(Math.random() * choices.length)];
};

// Format timestamp for display
const formatTime = (timestamp) => {
  const date = new Date(timestamp);
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

// Format date for message grouping
const formatDate = (timestamp) => {
  const date = new Date(timestamp);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  
  if (date.toDateString() === today.toDateString()) {
    return "Today";
  } else if (date.toDateString() === yesterday.toDateString()) {
    return "Yesterday";
  }
  return date.toLocaleDateString([], { weekday: 'long', month: 'short', day: 'numeric' });
};

// Check if two messages are on different days
const isDifferentDay = (msg1, msg2) => {
  if (!msg1 || !msg2) return true;
  const date1 = new Date(msg1.timestamp).toDateString();
  const date2 = new Date(msg2.timestamp).toDateString();
  return date1 !== date2;
};

// Format session date
const formatSessionDate = (dateStr) => {
  const date = new Date(dateStr);
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  
  if (date.toDateString() === today.toDateString()) {
    return "Today";
  } else if (date.toDateString() === tomorrow.toDateString()) {
    return "Tomorrow";
  }
  return date.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });
};

// Message bubble component
const formatLineupDay = (isoDate) => {
  if (!isoDate) return "";
  const parsed = new Date(`${isoDate}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) return isoDate;
  const today = new Date();
  const todayKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const tomorrowKey = `${tomorrow.getFullYear()}-${String(tomorrow.getMonth() + 1).padStart(2, "0")}-${String(tomorrow.getDate()).padStart(2, "0")}`;
  if (isoDate === todayKey) return "Today";
  if (isoDate === tomorrowKey) return "Tomorrow";
  return parsed.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
};

const nextLineupSession = (movie) => {
  const sessions = Array.isArray(movie?.sessions) ? movie.sessions : [];
  if (!sessions.length) return null;
  return [...sessions].sort((a, b) => {
    const left = `${a?.date || ""} ${a?.time || ""}`;
    const right = `${b?.date || ""} ${b?.time || ""}`;
    return left.localeCompare(right);
  })[0];
};

const LINEUP_VISIBLE_DEFAULT = 4;

const MovieLineupCards = ({ lineup, onSelectMovie }) => {
  const [expandedSections, setExpandedSections] = useState({});
  if (!lineup?.movies?.length) return null;
  const sections = [];
  lineup.movies.forEach((movie) => {
    const section = movie.section || lineup.title || "Cinema line-up";
    const existing = sections.find((entry) => entry.label === section);
    if (existing) {
      existing.movies.push(movie);
    } else {
      sections.push({ label: section, movies: [movie] });
    }
  });
  const isAdvanceSection = (label) => /advance|upcoming/i.test(String(label || ""));
  return (
    <div className="mt-2 space-y-2" data-testid="movie-lineup-cards">
      {sections.map((section, sectionIndex) => {
        const advance = isAdvanceSection(section.label);
        const sectionKey = section.label;
        const defaultOpen = !advance && sectionIndex === 0;
        const isOpen = expandedSections[sectionKey] ?? defaultOpen;
        const showAll = expandedSections[`${sectionKey}::all`] === true;
        const visibleMovies = showAll
          ? section.movies
          : section.movies.slice(0, LINEUP_VISIBLE_DEFAULT);
        const hiddenCount = Math.max(0, section.movies.length - visibleMovies.length);
        return (
          <div key={sectionKey} className="space-y-1.5">
            <button
              type="button"
              onClick={() =>
                setExpandedSections((prev) => ({
                  ...prev,
                  [sectionKey]: !isOpen,
                }))
              }
              className="w-full flex items-center justify-between gap-2 min-h-10 px-1 text-left"
              aria-expanded={isOpen}
            >
              <span className="text-base font-semibold text-[#EC1A23]">
                {section.label}
                <span className="ml-1 font-medium text-[var(--cb-ink-muted)]">
                  ({section.movies.length})
                </span>
              </span>
              <span className="text-base text-[var(--cb-ink-muted)]">{isOpen ? "Hide" : "Show"}</span>
            </button>
            {isOpen ? (
              <div className="border border-gray-200 rounded-lg overflow-hidden divide-y divide-gray-100">
                {visibleMovies.map((movie) => {
                  const nextSession = nextLineupSession(movie);
                  const dayLabel = nextSession ? formatLineupDay(nextSession.date) : "";
                  return (
                    <div
                      key={`${sectionKey}-${movie.id || movie.title}`}
                      className="flex items-center gap-2 px-2 py-1.5 bg-white"
                      data-testid={`lineup-movie-${movie.id || movie.title}`}
                    >
                      <button
                        type="button"
                        onClick={() => onSelectMovie && onSelectMovie(movie)}
                        className="w-9 h-12 bg-gray-100 rounded overflow-hidden flex-shrink-0 focus:outline-none"
                        aria-label={`Showtimes for ${movie.title}`}
                      >
                        {movie.poster_url ? (
                          <img src={movie.poster_url} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-[#EC1A23]/15 to-[#561D1A]/20 flex items-center justify-center">
                            <Film className="h-4 w-4 text-[#EC1A23]" aria-hidden="true" />
                          </div>
                        )}
                      </button>
                      <div className="flex-1 min-w-0">
                        <button
                          type="button"
                          onClick={() => onSelectMovie && onSelectMovie(movie)}
                          className="text-left w-full"
                        >
                          <h4 className="text-base font-semibold text-[var(--cb-ink)] truncate">{movie.title}</h4>
                          <p className="text-base text-[var(--cb-ink-muted)] truncate">
                            {[movie.rating, movie.duration_minutes ? `${movie.duration_minutes} min` : null, movie.genre]
                              .filter(Boolean)
                              .join(" · ")}
                          </p>
                        </button>
                      </div>
                      {nextSession ? (
                        <button
                          type="button"
                          onClick={() => onSelectMovie && onSelectMovie(movie, nextSession)}
                          className="shrink-0 max-w-[42%] min-h-10 px-2 py-1 rounded-md bg-[#EC1A23] text-white text-base font-semibold leading-tight text-right"
                          data-testid={`lineup-next-session-${movie.id || movie.title}`}
                        >
                          <span className="block truncate">{dayLabel}</span>
                          <span className="block truncate opacity-95">{nextSession.time}</span>
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => onSelectMovie && onSelectMovie(movie)}
                          className="shrink-0 text-base font-semibold text-[#EC1A23] px-1"
                        >
                          Open
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : null}
            {isOpen && hiddenCount > 0 ? (
              <button
                type="button"
                onClick={() =>
                  setExpandedSections((prev) => ({
                    ...prev,
                    [`${sectionKey}::all`]: true,
                  }))
                }
                className="w-full min-h-10 text-base font-semibold text-[#EC1A23]"
                data-testid={`lineup-show-more-${sectionKey}`}
              >
                Show {hiddenCount} more
              </button>
            ) : null}
            {isOpen && showAll && section.movies.length > LINEUP_VISIBLE_DEFAULT ? (
              <button
                type="button"
                onClick={() =>
                  setExpandedSections((prev) => ({
                    ...prev,
                    [`${sectionKey}::all`]: false,
                  }))
                }
                className="w-full min-h-10 text-base font-semibold text-[var(--cb-ink-muted)]"
              >
                Show fewer
              </button>
            ) : null}
          </div>
        );
      })}
    </div>
  );
};

const MessageBubble = ({ message, isUser, showTail, isRead, onSelectMovie }) => {
  const lineup = !isUser && message?.ui?.type === "movie_lineup" ? message.ui : null;
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"} mb-1`}>
      <div className={`relative ${lineup ? "max-w-[95%] w-full" : "max-w-[85%]"} ${isUser ? "order-1" : ""}`}>
        <div
          className={`px-3 py-2 ${
            isUser
              ? "bg-[#d64548] text-white rounded-l-lg rounded-tr-lg"
              : "bg-white text-gray-800 rounded-r-lg rounded-tl-lg shadow-sm border border-gray-100"
          } ${showTail ? (isUser ? "rounded-br-none" : "rounded-bl-none") : "rounded-lg"}`}
          style={{ wordBreak: "break-word" }}
        >
          <p className="text-sm whitespace-pre-wrap leading-relaxed" dangerouslySetInnerHTML={{ __html: linkifyText(message.content) }} />
          {lineup ? <MovieLineupCards lineup={lineup} onSelectMovie={onSelectMovie} /> : null}
          <div className={`flex items-center justify-end gap-1 mt-1 ${isUser ? "text-white/70" : "text-gray-400"}`}>
            <span className="text-[10px]">{formatTime(message.timestamp)}</span>
            {isUser && (
              <span className="ml-1">
                {isRead ? <CheckCheck className="h-3 w-3 text-blue-300" /> : <Check className="h-3 w-3" />}
              </span>
            )}
          </div>
        </div>
        {showTail && (
          <div 
            className={`absolute bottom-0 ${isUser ? "right-0 translate-x-1" : "left-0 -translate-x-1"}`}
            style={{
              width: 0, height: 0, borderStyle: "solid",
              borderWidth: isUser ? "0 0 8px 8px" : "0 8px 8px 0",
              borderColor: isUser ? "transparent transparent transparent #d64548" : "transparent white transparent transparent",
            }}
          />
        )}
      </div>
    </div>
  );
};

// Typing indicator
const TypingIndicator = () => (
  <div className="flex justify-start mb-2">
    <div className="bg-white rounded-2xl rounded-bl-none px-4 py-3 shadow-sm border border-gray-100">
      <div className="flex gap-1 items-center">
        <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
        <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
        <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
      </div>
    </div>
  </div>
);

// Date separator
const DateSeparator = ({ date }) => (
  <div className="flex items-center justify-center my-4">
    <div className="bg-white text-gray-500 text-xs px-3 py-1 rounded-lg shadow-sm border border-gray-100">
      {date}
    </div>
  </div>
);

// Smart Suggestions Component
const SmartSuggestions = ({ suggestions, onSelect, loading }) => {
  if (loading) {
    return (
      <div className="absolute bottom-full left-0 right-0 mb-1 bg-white rounded-lg shadow-lg border border-gray-200 p-3">
        <div className="flex items-center gap-2 text-gray-500 text-sm">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span>Finding suggestions...</span>
        </div>
      </div>
    );
  }
  
  if (!suggestions || suggestions.length === 0) return null;
  
  return (
    <div className="absolute bottom-full left-0 right-0 mb-1 bg-white rounded-lg shadow-lg border border-gray-200 max-h-48 overflow-y-auto z-50">
      <div className="p-2 border-b border-gray-100 flex items-center gap-2 text-xs text-gray-500">
        <Sparkles className="h-3 w-3 text-amber-500" />
        <span>Smart suggestions</span>
      </div>
      {suggestions.map((item, index) => (
        <button
          key={item.id || index}
          onClick={() => onSelect(item)}
          className="w-full px-3 py-2 text-left hover:bg-gray-50 flex items-center gap-3 border-b border-gray-50 last:border-0"
        >
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-white ${
            item.type === 'store' ? 'bg-gradient-to-br from-blue-500 to-blue-600' :
            item.type === 'service' ? 'bg-gradient-to-br from-amber-500 to-orange-600' :
            item.type === 'restaurant' ? 'bg-gradient-to-br from-emerald-500 to-emerald-600' :
            'bg-gradient-to-br from-purple-500 to-purple-600'
          }`}>
            {item.type === 'store' ? <Store className="h-4 w-4" /> :
             item.type === 'service' ? <BriefcaseBusiness className="h-4 w-4" /> :
             item.type === 'restaurant' ? <Utensils className="h-4 w-4" /> :
             <Film className="h-4 w-4" />}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-[#561D1A] truncate">{item.name || item.title}</p>
            <p className="text-xs text-gray-500 truncate">
              {item.type === 'store' || item.type === 'service' ? item.category :
               item.type === 'restaurant' ? item.cuisine_type :
               item.genre} • Floor {item.floor || 'N/A'}
            </p>
          </div>
        </button>
      ))}
    </div>
  );
};

const CINEMA_BOOKING_STEPS = [
  { id: "film", label: "Film" },
  { id: "show", label: "Show" },
  { id: "seats", label: "Seats" },
  { id: "snacks", label: "Snacks" },
  { id: "pay", label: "Pay" },
];

/** Compact cinema booking progress — visual only, does not change booking state. */
const BookingStepper = ({ currentStepId }) => {
  const currentIndex = Math.max(
    0,
    CINEMA_BOOKING_STEPS.findIndex((step) => step.id === currentStepId)
  );
  return (
    <div
      className="px-3 py-2 bg-white border-b border-[var(--cb-chip-border)] shrink-0"
      data-testid="booking-stepper"
      aria-label="Booking progress"
    >
      <div className="flex items-center justify-between gap-1">
        {CINEMA_BOOKING_STEPS.map((step, index) => {
          const done = index < currentIndex;
          const active = index === currentIndex;
          return (
            <div key={step.id} className="flex items-center flex-1 min-w-0">
              <div className="flex flex-col items-center gap-1 min-w-0 w-full">
                <span
                  className={`w-6 h-6 rounded-full text-[10px] font-bold flex items-center justify-center border ${
                    active
                      ? "bg-[#EC1A23] border-[#EC1A23] text-white"
                      : done
                        ? "bg-[#EC1A23]/15 border-[#EC1A23] text-[#EC1A23]"
                        : "bg-gray-100 border-gray-200 text-gray-400"
                  }`}
                >
                  {done ? <Check className="h-3.5 w-3.5" aria-hidden="true" /> : index + 1}
                </span>
                <span
                  className={`text-[10px] font-semibold truncate w-full text-center ${
                    active ? "text-[#EC1A23]" : done ? "text-[#561D1A]" : "text-gray-400"
                  }`}
                >
                  {step.label}
                </span>
              </div>
              {index < CINEMA_BOOKING_STEPS.length - 1 ? (
                <div
                  className={`h-0.5 flex-1 mx-0.5 mb-4 rounded-full ${
                    done ? "bg-[#EC1A23]" : "bg-gray-200"
                  }`}
                  aria-hidden="true"
                />
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const CINEMA_CHAIR_COLORS = {
  available: { fill: '#A7F3D0', stroke: '#059669', text: '#065F46' },
  vip: { fill: '#FDE68A', stroke: '#D97706', text: '#92400E' },
  selected: { fill: '#EC1A23', stroke: '#9F1239', text: '#FFFFFF' },
  held: { fill: '#E7E0D8', stroke: '#A89890', text: '#8A7A74' },
  occupied: { fill: '#C4B8B0', stroke: '#9A8B84', text: '#7A6E68' },
};

/** Simple rounded-square seat tile (number + state colors only). */
const CinemaChairIcon = ({ fill, stroke, number, textFill, className }) => (
  <svg viewBox="0 0 32 32" className={className} aria-hidden="true">
    <rect x="1.5" y="1.5" width="29" height="29" rx="7" fill={fill} stroke={stroke} strokeWidth="1.8" />
    {number !== undefined && number !== null && (
      <text
        x="16"
        y="20.5"
        textAnchor="middle"
        fontSize="11"
        fontWeight="800"
        fill={textFill}
        fontFamily="Inter, system-ui, sans-serif"
      >
        {number}
      </text>
    )}
  </svg>
);

const getCinemaChairStyle = (isOccupied, isHeld, isSelected, isVipSeat) => {
  if (isOccupied) return CINEMA_CHAIR_COLORS.occupied;
  if (isHeld) return CINEMA_CHAIR_COLORS.held;
  if (isSelected) return CINEMA_CHAIR_COLORS.selected;
  if (isVipSeat) return CINEMA_CHAIR_COLORS.vip;
  return CINEMA_CHAIR_COLORS.available;
};

// Seat Selection Component
const SeatSelection = ({ seatPlan, selectedSeats, onSeatSelect, is3D, isVip }) => {
  const mapRef = useRef(null);
  if (!seatPlan?.rows) return null;

  const rows = seatPlan.rows;
  const hasVipSeats = Boolean(isVip) || rows.some((row) =>
    (row.seats || []).some((seat) => seat.type === 'vip')
  );
  const hasHeldSeats = rows.some((row) =>
    (row.seats || []).some((seat) => seat.hold)
  );

  const focusSeatAt = (rowIndex, colIndex) => {
    const button = mapRef.current?.querySelector(`[data-seat-pos="${rowIndex}-${colIndex}"]`);
    if (button && !button.disabled) {
      button.focus();
      return true;
    }
    return false;
  };

  const handleSeatKeyDown = (event, rowIndex, colIndex) => {
    const key = event.key;
    if (key !== "ArrowLeft" && key !== "ArrowRight" && key !== "ArrowUp" && key !== "ArrowDown") {
      return;
    }
    event.preventDefault();
    if (key === "ArrowLeft" || key === "ArrowRight") {
      const step = key === "ArrowRight" ? 1 : -1;
      const rowLen = (rows[rowIndex]?.seats || []).length;
      for (let col = colIndex + step; col >= 0 && col < rowLen; col += step) {
        if (focusSeatAt(rowIndex, col)) return;
      }
      return;
    }
    const step = key === "ArrowDown" ? 1 : -1;
    for (let row = rowIndex + step; row >= 0 && row < rows.length; row += step) {
      const rowLen = (rows[row]?.seats || []).length;
      if (rowLen <= 0) continue;
      const preferred = Math.min(colIndex, rowLen - 1);
      if (focusSeatAt(row, preferred)) return;
      for (let distance = 1; distance < rowLen; distance += 1) {
        if (preferred - distance >= 0 && focusSeatAt(row, preferred - distance)) return;
        if (preferred + distance < rowLen && focusSeatAt(row, preferred + distance)) return;
      }
    }
  };

  return (
    <div className="rounded-2xl overflow-hidden bg-[#4A2420]">
      <div className="px-4 pt-4 pb-2 text-center">
        <svg viewBox="0 0 400 36" className="w-full max-w-sm mx-auto" aria-hidden="true">
          <path
            d="M20 28 Q200 4 380 28"
            fill="none"
            stroke="#F5E6C8"
            strokeWidth="6"
            strokeLinecap="round"
          />
        </svg>
        <p className="text-[11px] font-extrabold tracking-[0.35em] text-[#F5E6C8] -mt-1">
          {(seatPlan.screen || 'SCREEN').toUpperCase()}
        </p>
      </div>

      {is3D && (
        <div className="mx-4 mb-3 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-2">
          <Glasses className="h-5 w-5 text-amber-600 glasses-3d-pulse" aria-hidden="true" />
          <div>
            <p className="text-sm font-medium text-amber-900">3D Movie</p>
            <p className="text-xs text-amber-800">Buy 3D glasses at the cinema counter</p>
          </div>
        </div>
      )}

      <div
        ref={mapRef}
        className="mx-3 mb-3 rounded-xl bg-[#F7F0E8] p-3 overflow-x-auto"
        role="group"
        aria-label="Seat map. Use arrow keys to move between seats, Enter or Space to select."
        data-testid="seat-map"
      >
        <div className="sr-only" aria-live="polite" aria-atomic="true">
          {selectedSeats.length === 0
            ? "No seats selected"
            : `${selectedSeats.length} seat${selectedSeats.length === 1 ? "" : "s"} selected: ${selectedSeats.join(", ")}`}
        </div>
        <div className="space-y-0.5 min-w-max mx-auto">
          {rows.map((row, rowIndex) => (
            <div key={row.row} className="flex items-center gap-1 justify-center" role="row">
              <span className="w-5 text-[11px] font-extrabold text-[var(--cb-ink-muted)] text-center" aria-hidden="true">{row.row}</span>
              <div className="flex items-center">
                {(row.seats || []).map((seat, index) => {
                  const seatKey = seat.id || `${row.row}${seat.number}`;
                  const isSelected = selectedSeats.includes(seatKey);
                  const isHeld = Boolean(seat.hold);
                  const isOccupied = seat.status === 'occupied' && !isHeld;
                  const isVipSeat = seat.type === 'vip' || (Boolean(isVip) && seat.type !== 'standard');
                  const prevNumber = Number((row.seats[index - 1] || {}).number);
                  const currentNumber = Number(seat.number);
                  const showAisle = index > 0 && Number.isFinite(prevNumber) && Number.isFinite(currentNumber) && (currentNumber - prevNumber > 1);
                  const colors = getCinemaChairStyle(isOccupied, isHeld, isSelected, isVipSeat);
                  const statusLabel = isOccupied ? "occupied" : isHeld ? "held by another guest" : isSelected ? "selected" : isVipSeat ? "VIP available" : "available";
                  const unavailable = isOccupied || isHeld;

                  return (
                    <span key={seatKey} className="inline-flex items-center">
                      {showAisle && <span className="w-3 sm:w-4" aria-hidden="true" />}
                      <button
                        type="button"
                        data-seat-pos={`${rowIndex}-${index}`}
                        data-seat-key={seatKey}
                        disabled={unavailable}
                        aria-pressed={isSelected}
                        aria-label={`Seat ${row.row}${seat.number}, ${statusLabel}${seat.price ? `, KES ${seat.price}` : ""}`}
                        onClick={() => onSeatSelect(seatKey)}
                        onKeyDown={(event) => handleSeatKeyDown(event, rowIndex, index)}
                        className={`w-8 h-8 m-0.5 p-0 bg-transparent border-0 leading-none rounded-sm cb-seat-tile ${
                          unavailable ? 'cursor-not-allowed opacity-90' : 'cursor-pointer'
                        } ${isSelected ? 'cb-seat-selected' : ''}`}
                        title={`Seat ${row.row}${seat.number} · ${statusLabel}${seat.price ? ` · KES ${seat.price}` : ""}`}
                      >
                        <CinemaChairIcon
                          fill={colors.fill}
                          stroke={colors.stroke}
                          textFill={colors.text}
                          number={seat.number}
                          className="w-full h-full"
                        />
                      </button>
                    </span>
                  );
                })}
              </div>
              <span className="w-5 text-[11px] font-extrabold text-[var(--cb-ink-muted)] text-center" aria-hidden="true">{row.row}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="px-4 pb-4 flex flex-wrap justify-center gap-x-3 gap-y-2 text-[11px] font-semibold text-[#F5E6C8]" aria-hidden="true">
        <div className="flex items-center gap-1">
          <CinemaChairIcon fill={CINEMA_CHAIR_COLORS.available.fill} stroke={CINEMA_CHAIR_COLORS.available.stroke} className="w-4 h-4" />
          Available
        </div>
        {hasVipSeats && (
          <div className="flex items-center gap-1">
            <CinemaChairIcon fill={CINEMA_CHAIR_COLORS.vip.fill} stroke={CINEMA_CHAIR_COLORS.vip.stroke} className="w-4 h-4" />
            VIP
          </div>
        )}
        <div className="flex items-center gap-1">
          <CinemaChairIcon fill={CINEMA_CHAIR_COLORS.selected.fill} stroke={CINEMA_CHAIR_COLORS.selected.stroke} className="w-4 h-4" />
          Selected
        </div>
        {hasHeldSeats && (
          <div className="flex items-center gap-1">
            <CinemaChairIcon fill={CINEMA_CHAIR_COLORS.held.fill} stroke={CINEMA_CHAIR_COLORS.held.stroke} className="w-4 h-4" />
            Held
          </div>
        )}
        <div className="flex items-center gap-1">
          <CinemaChairIcon fill={CINEMA_CHAIR_COLORS.occupied.fill} stroke={CINEMA_CHAIR_COLORS.occupied.stroke} className="w-4 h-4" />
          Occupied
        </div>
      </div>
    </div>
  );
};

// Store/Restaurant Detail Component

  // Helper function to construct Westgate image URLs
  const getWestgateImageUrl = (filename) => {
    if (!filename) return '';
    if (filename.startsWith('http')) return filename;
    return `https://cms.westgate.co.ke/images/${encodeURIComponent(filename)}`;
  };

  const directoryLogoUrl = (item) => {
    const raw = (item && (item.logo_url || item.image_url)) || '';
    if (!raw || raw.includes('undefined')) return '';
    return getWestgateImageUrl(raw);
  };

  const compareByNameAsc = (left, right) =>
    String(left?.name || '').localeCompare(String(right?.name || ''), undefined, { sensitivity: 'base' });

  const compareByTitleAsc = (left, right) =>
    String(left?.title || '').localeCompare(String(right?.title || ''), undefined, { sensitivity: 'base' });

const ItemDetailView = ({ item, type, onBack, onBookTable }) => {
  if (!item) return null;
  
  const isRestaurant = type === 'restaurant';
  const bgColor = type === 'service'
    ? 'from-amber-500 to-orange-600'
    : isRestaurant
      ? 'from-emerald-500 to-emerald-600'
      : 'from-[#EC1A23] to-[#561D1A]';
  const hasLogo = Boolean(directoryLogoUrl(item));
  
  return (
    <div className="flex-1 overflow-y-auto bg-white">
      {/* Header with logo */}
      <div className={`bg-gradient-to-r ${bgColor} p-6 text-white`}>
        <div className="flex items-center gap-4">
          {hasLogo ? (
            <img 
              src={directoryLogoUrl(item)} 
              alt={item.name}
              className="w-20 h-20 rounded-xl object-cover bg-white"
              onError={(e) => {
                e.target.onerror = null;
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
          ) : null}
          <div 
            className={`w-20 h-20 bg-white/20 rounded-xl items-center justify-center text-3xl font-bold ${hasLogo ? 'hidden' : 'flex'}`}
          >
            {item.name?.charAt(0)}
          </div>
          <div className="flex-1">
            <h2 className="cb-page-title text-xl">{item.name}</h2>
            <p className="text-white/80 text-sm capitalize">{isRestaurant ? item.cuisine_type : item.category}</p>
            <div className="flex items-center gap-1 mt-1 text-white/80 text-sm">
              <MapPin className="h-4 w-4" aria-hidden="true" />
              <span>
                {normalizeFloorLabel(item.floor)}
                {item.unit_number ? `, Unit ${item.unit_number}` : ""}
              </span>
            </div>
          </div>
        </div>
      </div>
      
      {/* Description */}
      {item.description && (
        <div className="p-4 border-b">
          <h3 className="font-semibold text-sm text-[var(--cb-ink-secondary)] mb-2">About</h3>
          <p className="text-sm text-[var(--cb-ink-secondary)] leading-relaxed">{item.description}</p>
        </div>
      )}
      
      {/* Contact Details */}
      <div className="p-4">
        <h3 className="font-semibold text-sm text-[var(--cb-ink-secondary)] mb-4 tracking-wide">Find & contact</h3>
        <div className="space-y-4">
          <a
            href={westgateMapsUrl(item)}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-4 p-3 min-h-11 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors group"
            data-testid="directory-maps-link"
          >
            <div className="w-11 h-11 bg-red-100 rounded-full flex items-center justify-center shrink-0">
              <MapPin className="h-5 w-5 text-[#EC1A23]" aria-hidden="true" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-[var(--cb-ink)]">Open in Google Maps</p>
              <p className="text-xs text-[var(--cb-ink-muted)]">
                {normalizeFloorLabel(item.floor)}
                {item.unit_number ? ` · Unit ${item.unit_number}` : ""} · Westgate Mall
              </p>
            </div>
            <ExternalLink className="h-4 w-4 text-gray-400 group-hover:text-[#EC1A23] shrink-0" aria-hidden="true" />
          </a>

          {/* Phone */}
          {item.phone && (
            <a 
              href={`tel:${item.phone}`}
              className="flex items-center gap-4 p-3 min-h-11 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors group"
            >
              <div className="w-11 h-11 bg-blue-100 rounded-full flex items-center justify-center shrink-0">
                <Phone className="h-5 w-5 text-blue-700" aria-hidden="true" />
              </div>
              <div className="flex-1">
                <p className="font-medium text-[var(--cb-ink)]">{item.phone}</p>
                <p className="text-xs text-[var(--cb-ink-muted)]">Tap to call</p>
              </div>
              <ExternalLink className="h-4 w-4 text-gray-400 group-hover:text-blue-700" aria-hidden="true" />
            </a>
          )}
          
          {/* Email */}
          {item.email && (
            <a 
              href={`mailto:${item.email}`}
              className="flex items-center gap-4 p-3 min-h-11 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors group"
            >
              <div className="w-11 h-11 bg-purple-100 rounded-full flex items-center justify-center shrink-0">
                <Mail className="h-5 w-5 text-purple-700" aria-hidden="true" />
              </div>
              <div className="flex-1">
                <p className="font-medium text-[var(--cb-ink)] text-sm break-all">{item.email}</p>
                <p className="text-xs text-[var(--cb-ink-muted)]">Tap to email</p>
              </div>
              <ExternalLink className="h-4 w-4 text-gray-400 group-hover:text-purple-700" aria-hidden="true" />
            </a>
          )}
          
          {/* Opening Hours */}
          {item.opening_hours && (
            <div className="flex items-center gap-4 p-3 min-h-11 bg-gray-50 rounded-lg">
              <div className="w-11 h-11 bg-amber-100 rounded-full flex items-center justify-center shrink-0">
                <Clock className="h-5 w-5 text-amber-700" aria-hidden="true" />
              </div>
              <div className="flex-1">
                <p className="font-medium text-[var(--cb-ink)]">{item.opening_hours}</p>
                <p className="text-xs text-[var(--cb-ink-muted)]">Operating hours</p>
              </div>
            </div>
          )}
          
          {/* Website */}
          {item.website && (
            <a 
              href={item.website.startsWith('http') ? item.website : `https://${item.website}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-4 p-3 min-h-11 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors group"
            >
              <div className="w-11 h-11 bg-emerald-100 rounded-full flex items-center justify-center shrink-0">
                <Globe className="h-5 w-5 text-emerald-700" aria-hidden="true" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-[var(--cb-ink)]">Visit Website</p>
                <p className="text-xs text-[var(--cb-ink-muted)] truncate">{item.website}</p>
              </div>
              <ExternalLink className="h-4 w-4 text-gray-400 group-hover:text-emerald-700 shrink-0" aria-hidden="true" />
            </a>
          )}
        </div>
        
        {/* Social Media */}
        {(item.facebook || item.instagram || item.twitter) && (
          <div className="mt-6">
            <h3 className="font-semibold text-sm text-[var(--cb-ink-secondary)] mb-3 tracking-wide">Follow us</h3>
            <div className="flex gap-3">
              {item.facebook && (
                <a 
                  href={item.facebook.startsWith('http') ? item.facebook : `https://facebook.com/${item.facebook}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center text-white hover:bg-blue-700 transition-colors"
                >
                  <Facebook className="h-6 w-6" />
                </a>
              )}
              {item.instagram && (
                <a 
                  href={item.instagram.startsWith('http') ? item.instagram : `https://instagram.com/${item.instagram}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 bg-gradient-to-br from-purple-600 to-pink-500 rounded-full flex items-center justify-center text-white hover:opacity-90 transition-opacity"
                >
                  <Instagram className="h-6 w-6" />
                </a>
              )}
              {item.twitter && (
                <a 
                  href={item.twitter.startsWith('http') ? item.twitter : `https://twitter.com/${item.twitter}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 bg-sky-500 rounded-full flex items-center justify-center text-white hover:bg-sky-600 transition-colors"
                >
                  <Twitter className="h-6 w-6" />
                </a>
              )}
            </div>
          </div>
        )}
        
        {/* Reservations for restaurants */}
        {isRestaurant && item.accepts_reservations && (
          <div className="mt-6 p-4 bg-[#F3F4F6] rounded-lg border border-[#E5E7EB] space-y-3">
            <div className="flex items-center gap-2 text-[#561D1A]">
              <CheckCircle className="h-5 w-5 text-[#EC1A23]" />
              <span className="cb-section-title text-sm">Table booking available</span>
            </div>
            <p className="text-xs text-[#6B3A36] leading-relaxed">
              Request a table for a date and time. The restaurant will confirm your booking.
            </p>
            {onBookTable ? (
              <button
                type="button"
                onClick={() => onBookTable(item)}
                className="w-full py-3 min-h-11 bg-gradient-to-r from-[#EC1A23] to-[#561D1A] text-white text-sm font-bold tracking-wide rounded-lg hover:opacity-95 transition-opacity"
                data-testid="book-table-btn"
              >
                Book a table
              </button>
            ) : item.phone ? (
              <p className="text-xs text-[#6B3A36]">Call {item.phone} to book a table</p>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
};

const WEEKDAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const dateKey = (year, month, day) =>
  `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;

const monthOfKey = (key) => ({
  year: Number(String(key).slice(0, 4)),
  month: Number(String(key).slice(5, 7)) - 1,
});

const monthIndex = ({ year, month }) => year * 12 + month;

const BookingCalendar = ({ dates, slotsByDate, selected, onSelect }) => {
  const openDates = dates || [];
  const today = openDates[0] || "";
  const lastDate = openDates[openDates.length - 1] || today;
  const [view, setView] = useState(() => monthOfKey(selected || today));

  if (!today) {
    return <p className="text-sm text-[#6B3A36]">No dates available for booking.</p>;
  }

  const firstMonth = monthOfKey(today);
  const lastMonth = monthOfKey(lastDate);
  const canGoBack = monthIndex(view) > monthIndex(firstMonth);
  const canGoForward = monthIndex(view) < monthIndex(lastMonth);
  const leadingBlanks = (new Date(view.year, view.month, 1).getDay() + 6) % 7;
  const daysInMonth = new Date(view.year, view.month + 1, 0).getDate();

  return (
    <div className="border border-[#E5E7EB] rounded-lg overflow-hidden">
      <div className="flex items-center justify-between px-3 py-2 bg-[#F3F4F6] border-b border-[#E5E7EB]">
        <button
          type="button"
          disabled={!canGoBack}
          onClick={() => setView(monthOfKey(dateKey(view.year, view.month - 1, 1)))}
          className="p-1 text-[#561D1A] disabled:opacity-30"
          aria-label="Previous month"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <span className="cb-section-title text-sm">
          {new Date(view.year, view.month, 1).toLocaleDateString("en-GB", { month: "long", year: "numeric" })}
        </span>
        <button
          type="button"
          disabled={!canGoForward}
          onClick={() => setView(monthOfKey(dateKey(view.year, view.month + 1, 1)))}
          className="p-1 text-[#561D1A] disabled:opacity-30"
          aria-label="Next month"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
      <div className="grid grid-cols-7 gap-1 px-2 pt-2 text-[10px] font-bold uppercase tracking-wide text-[#8A5A55]">
        {WEEKDAY_LABELS.map((label) => (
          <span key={label} className="text-center py-1">{label}</span>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1 p-2">
        {Array.from({ length: leadingBlanks }, (_, index) => (
          <span key={`blank-${index}`} />
        ))}
        {Array.from({ length: daysInMonth }, (_, index) => {
          const day = index + 1;
          const key = dateKey(view.year, view.month, day);
          const bookable = openDates.includes(key) && (slotsByDate?.[key] || []).length > 0;
          const isSelected = key === selected;
          return (
            <button
              key={key}
              type="button"
              disabled={!bookable}
              onClick={() => onSelect(key)}
              data-testid={`booking-date-${key}`}
              className={`h-9 rounded-md text-sm font-semibold transition-colors ${
                isSelected
                  ? "bg-[#EC1A23] text-white"
                  : bookable
                    ? "bg-white text-[#561D1A] border border-[#E5E7EB] hover:bg-[#F3F4F6]"
                    : "text-[#C4ADAB] cursor-not-allowed"
              } ${key === today && !isSelected ? "ring-1 ring-[#EC1A23]" : ""}`}
            >
              {day}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const TableBookingPanel = ({ restaurant, visitor, visitorAuthenticated, onRequireAuth, onBack, onSubmitted }) => {
  const [options, setOptions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(null);
  const [form, setForm] = useState({
    booking_date: "",
    booking_time: "",
    guests: 2,
    special_requests: "",
  });

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const response = await axios.get(
          `${API_URL}/api/public/restaurants/${restaurant.id}/booking-options`
        );
        if (cancelled) return;
        setOptions(response.data);
        const firstDate = (response.data.dates || []).find((day) => (response.data.slots_by_date?.[day] || []).length > 0)
          || response.data.dates?.[0]
          || "";
        const firstSlot = (response.data.slots_by_date?.[firstDate] || [])[0] || "";
        setForm((current) => ({
          ...current,
          booking_date: firstDate,
          booking_time: firstSlot,
          guests: Math.min(2, response.data.booking_max_party_size || 2),
        }));
      } catch (err) {
        if (!cancelled) {
          setError(apiErrorMessage(err, "Could not load booking options"));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [restaurant.id]);

  const slots = options?.slots_by_date?.[form.booking_date] || [];
  const maxGuests = options?.booking_max_party_size || 12;

  const submitBooking = async (event) => {
    event.preventDefault();
    if (!visitorAuthenticated) {
      onRequireAuth();
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const response = await axios.post(
        `${API_URL}/api/public/bookings/restaurant`,
        {
          restaurant_id: restaurant.id,
          booking_date: form.booking_date,
          booking_time: form.booking_time,
          guests: Number(form.guests),
          special_requests: form.special_requests,
        },
        { withCredentials: true }
      );
      setSuccess(response.data);
      onSubmitted(response.data);
    } catch (err) {
      const detail = apiErrorMessage(err, "Could not submit booking");
      if (err?.response?.status === 401) {
        onRequireAuth();
        setError("Please verify your phone to book a table.");
      } else {
        setError(detail);
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center bg-white">
        <Loader2 className="h-8 w-8 animate-spin text-[#EC1A23]" />
      </div>
    );
  }

  if (success) {
    return (
      <div className="flex-1 overflow-y-auto bg-white p-5 space-y-4">
        <div className="text-center space-y-2">
          <CheckCircle className="h-12 w-12 text-[#EC1A23] mx-auto" />
          <h2 className="cb-headline-lg">Request sent</h2>
          <p className="text-sm text-[#6B3A36]">
            Ref <span className="font-mono font-bold text-[#561D1A]">{success.reference_id}</span> is pending confirmation from {restaurant.name}.
          </p>
          <p className="text-xs text-[#8A5A55]">
            We will notify you by WhatsApp and email once it is confirmed.
          </p>
        </div>
        <button
          type="button"
          onClick={onBack}
          className="w-full py-3 min-h-11 bg-[#F3F4F6] text-[#561D1A] text-sm font-bold tracking-wide rounded-lg"
        >
          Back to restaurant
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submitBooking} className="flex-1 overflow-y-auto bg-white">
      <div className="p-4 border-b border-[#E5E7EB]">
        <h2 className="cb-section-title">Book a table at {restaurant.name}</h2>
        <p className="text-xs text-[#8A5A55] mt-1">
          Booking as {visitor?.name || "verified guest"} · {visitor?.phone || ""}
        </p>
      </div>
      <div className="p-4 space-y-4">
        {error ? <div className="p-3 bg-[#FDECEC] text-[#B3151C] text-sm rounded-lg">{error}</div> : null}
        <div>
          <label className="text-[11px] font-bold tracking-wide text-[#6B3A36]">Date</label>
          <p className="text-[11px] text-[#8A5A55] mb-2">Open for the next {options?.booking_advance_days || 7} days</p>
          <BookingCalendar
            dates={options?.dates}
            slotsByDate={options?.slots_by_date}
            selected={form.booking_date}
            onSelect={(day) => {
              const nextSlots = options?.slots_by_date?.[day] || [];
              setForm({ ...form, booking_date: day, booking_time: nextSlots[0] || "" });
            }}
          />
        </div>
        <div>
          <label className="text-[11px] font-bold tracking-wide text-[#6B3A36]">Time</label>
          <div className="mt-2 grid grid-cols-3 gap-2">
            {slots.length === 0 ? (
              <p className="col-span-3 text-sm text-[#8A5A55]">No times left for this date.</p>
            ) : slots.map((slot) => (
              <button
                key={slot}
                type="button"
                onClick={() => setForm({ ...form, booking_time: slot })}
                className={`py-2 rounded-lg text-sm font-semibold border transition-colors ${
                  form.booking_time === slot
                    ? "bg-[#EC1A23] text-white border-[#EC1A23]"
                    : "bg-white text-[#561D1A] border-[#E5E7EB] hover:bg-[#F3F4F6]"
                }`}
              >
                {slot}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="text-[11px] font-bold tracking-wide text-[#6B3A36]">Guests (max {maxGuests})</label>
          <input
            type="number"
            min={1}
            max={maxGuests}
            required
            value={form.guests}
            onChange={(event) => setForm({ ...form, guests: event.target.value })}
            className="mt-1 w-full px-3 py-3 bg-[#F3F4F6] border border-[#E5E7EB] rounded-lg text-sm text-[#561D1A]"
          />
        </div>
        <div>
          <label className="text-[11px] font-bold tracking-wide text-[#6B3A36]">Special requests</label>
          <textarea
            value={form.special_requests}
            onChange={(event) => setForm({ ...form, special_requests: event.target.value })}
            rows={3}
            placeholder="Birthday, window seat, high chair..."
            className="mt-1 w-full px-3 py-3 bg-[#F3F4F6] border border-[#E5E7EB] rounded-lg text-sm text-[#561D1A]"
          />
        </div>
        <button
          type="submit"
          disabled={submitting || !form.booking_time}
          className="w-full py-3 min-h-11 bg-gradient-to-r from-[#EC1A23] to-[#561D1A] text-white text-sm font-bold tracking-wide rounded-lg disabled:opacity-50"
        >
          {submitting ? "Submitting..." : "Request table"}
        </button>
        <p className="text-[11px] text-[#8A5A55] text-center">
          Free reservation request. The restaurant confirms before your table is held.
        </p>
      </div>
    </form>
  );
};

const IdleSessionNotice = ({ remainingSeconds }) => (
  <div
    className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-[var(--cb-overlay,#561D1A8C)]"
    role="alertdialog"
    aria-modal="true"
    aria-labelledby="idle-session-title"
    aria-describedby="idle-session-desc"
    data-testid="idle-session-notice"
  >
    <div className="bg-white rounded-xl w-full max-w-sm overflow-hidden shadow-xl">
      <div className="bg-gradient-to-r from-[#EC1A23] to-[#561D1A] p-4 text-white text-center">
        <h2 id="idle-session-title" className="cb-page-title text-lg">Session ending</h2>
      </div>
      <div className="p-5 text-center space-y-3">
        <p id="idle-session-desc" className="text-sm text-[#561D1A] font-medium leading-relaxed">
          The Assistant has been idle for 3 minutes. Your session will close and return to the home screen so the next visitor can start fresh.
        </p>
        <p
          className="text-4xl font-mono font-bold text-[#EC1A23]"
          role="timer"
          aria-live="assertive"
          aria-atomic="true"
        >
          {remainingSeconds}
        </p>
        <p className="text-xs text-[var(--cb-ink-muted)]">
          Returning to home in {remainingSeconds} second{remainingSeconds === 1 ? "" : "s"}
        </p>
        <p className="text-xs text-[#561D1A] font-medium">Tap anywhere if you are still here</p>
      </div>
    </div>
  </div>
);

// Payment Modal with seat hold (7 mins), concessions, and M-Pesa
const PaymentModal = ({
  movie,
  session,
  seats,
  seatPlan,
  reservationId,
  expiresAt,
  visitor,
  onClose,
  onPaymentComplete,
  onHoldExpired,
  onAwaitingMpesa,
}) => {
  const [step, setStep] = useState('concessions'); // concessions, form, processing, success
  const [formData, setFormData] = useState({
    name: visitor?.name || '',
    phone: formatPhoneForPayment(visitor?.phone || ''),
    email: visitor?.email || '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [transactionId, setTransactionId] = useState(null);
  const [chargedAmount, setChargedAmount] = useState(null);
  const [pricedConcessions, setPricedConcessions] = useState([]);
  const [serverTicketTotal, setServerTicketTotal] = useState(null);
  const [booking, setBooking] = useState(null);
  const [countdown, setCountdown] = useState(0);
  const [waitingHint, setWaitingHint] = useState(null);
  const [snackItems, setSnackItems] = useState([]);
  const [snackCategories, setSnackCategories] = useState([]);
  const [selectedSnackCategory, setSelectedSnackCategory] = useState('all');
  const [snackQuery, setSnackQuery] = useState('');
  const [snacksLoading, setSnacksLoading] = useState(true);
  const [snacksAvailable, setSnacksAvailable] = useState(false);
  const [selectedConcessions, setSelectedConcessions] = useState({});
  const pollIntervalRef = useRef(null);
  const pollInFlightRef = useRef(false);
  const expiredNotifiedRef = useRef(false);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key !== "Escape") return;
      if (step === "processing" || step === "success") return;
      onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose, step]);

  useEffect(() => {
    onAwaitingMpesa(step === "processing");
    return () => {
      onAwaitingMpesa(false);
    };
  }, [step, onAwaitingMpesa]);

  const is3D = session?.is_3d || movie?.is_3d || session?.screen?.includes('3D');

  const getConcessionTotal = () => {
    return Object.values(selectedConcessions).reduce((sum, item) => {
      return sum + (Number(item.catalogPrice) * Number(item.quantity));
    }, 0);
  };

  const getConcessionList = () => {
    return Object.values(selectedConcessions)
      .filter((item) => item.quantity > 0)
      .map((item) => ({
        id: item.id,
        name: item.name,
        quantity: item.quantity,
      }));
  };

  const serverConcessionTotal = pricedConcessions.reduce((sum, item) => {
    return sum + Number(item.line_total);
  }, 0);
  
  // Calculate total
  const calculateTotal = () => {
    let total = 0;
    seats.forEach(seatId => {
      const row = seatPlan?.rows?.find(r => r.seats.some(s => s.id === seatId));
      const seat = row?.seats?.find(s => s.id === seatId);
      const unitPrice = seat?.price ?? session?.price ?? seatPlan?.ticket_price;
      total += Number(unitPrice || 0);
    });
    // 3D glasses are sold separately at the counter — not included in online total
    total += getConcessionTotal();
    return total;
  };

  useEffect(() => {
    if (!expiresAt) {
      setCountdown(0);
      return undefined;
    }
    const tick = () => {
      const remaining = Math.max(
        0,
        Math.floor((new Date(expiresAt).getTime() - Date.now()) / 1000)
      );
      setCountdown(remaining);
      if (remaining === 0 && !expiredNotifiedRef.current && step !== 'success') {
        if (step === 'processing') {
          return;
        }
        expiredNotifiedRef.current = true;
        if (onHoldExpired) {
          onHoldExpired();
        }
      }
    };
    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [expiresAt, onHoldExpired, step]);

  useEffect(() => {
    let cancelled = false;
    const loadSnacks = async () => {
      setSnacksLoading(true);
      try {
        const response = await axios.get(`${API_URL}/api/public/cinema/snacks`);
        if (cancelled) return;
        const items = (response.data.items || []).map((item, index) => ({
          id: String(item.id || item.uuid || index),
          name: item.name || `Item ${index + 1}`,
          price: Number(item.price || 0),
          description: item.description || '',
          image_url: item.image_url || null,
          category_id: String(item.category_id || item.category_uuid || 'other'),
          category_name: item.category_name || 'Other',
        })).filter((item) => item.price > 0 && item.name);
        const categories = (response.data.categories || []).map((cat) => ({
          id: String(cat.id),
          name: cat.name || 'Other',
        }));
        const fallbackCategories = [];
        const seen = new Set(categories.map((c) => c.id));
        items.forEach((item) => {
          if (!seen.has(item.category_id)) {
            seen.add(item.category_id);
            fallbackCategories.push({ id: item.category_id, name: item.category_name });
          }
        });
        setSnackItems(items);
        setSnackCategories(categories.length > 0 ? categories : fallbackCategories);
        setSnacksAvailable(Boolean(response.data.success) && items.length > 0);
        setSelectedSnackCategory('all');
      } catch (err) {
        if (!cancelled) {
          setSnackItems([]);
          setSnackCategories([]);
          setSnacksAvailable(false);
        }
      } finally {
        if (!cancelled) setSnacksLoading(false);
      }
    };
    loadSnacks();
    return () => { cancelled = true; };
  }, []);

  const holdHasEnded = () => Boolean(expiresAt) && new Date(expiresAt).getTime() <= Date.now();

  const leaveWaitingAfterHoldExpired = () => {
    clearInterval(pollIntervalRef.current);
    setWaitingHint(null);
    expiredNotifiedRef.current = true;
    if (onHoldExpired) {
      onHoldExpired();
    }
  };

  // Poll payment status — backend queries Safaricom so cancel/fail/hold expiry is not stuck on pending
  const pollPaymentStatus = useCallback(async () => {
    if (!transactionId || pollInFlightRef.current) return;
    pollInFlightRef.current = true;
    try {
      const response = await axios.get(`${API_URL}/api/public/mpesa/status/${transactionId}`);
      const status = response.data.status;
      const paidAwaitingReceipt = Number(response.data.stk_query_result_code) === 0;
      
      if (status === 'completed') {
        clearInterval(pollIntervalRef.current);
        if (!response.data.booking || !response.data.mpesa_receipt) {
          setPaymentStatus('failed');
          setWaitingHint(null);
          setError('M-Pesa reported payment, but tickets are not confirmed yet. Keep your M-Pesa SMS and contact the cinema — bookings confirm only after a verified M-Pesa callback.');
          setStep('form');
          return;
        }
        setPaymentStatus('success');
        setWaitingHint(null);
        setBooking(response.data.booking);
        setStep('success');
        if (onPaymentComplete) onPaymentComplete(response.data.booking);
      } else if (status === 'failed') {
        if (holdHasEnded()) {
          leaveWaitingAfterHoldExpired();
          return;
        }
        clearInterval(pollIntervalRef.current);
        setPaymentStatus('failed');
        setWaitingHint(null);
        setError(response.data.result_desc || 'M-Pesa payment failed or was cancelled. Your seats are still held until the timer ends — you can try again.');
        setStep('form');
      } else if (holdHasEnded() && paidAwaitingReceipt) {
        setWaitingHint('M-Pesa reported payment. Confirming tickets — do not close this screen.');
      } else if (holdHasEnded()) {
        leaveWaitingAfterHoldExpired();
      } else {
        setWaitingHint('Checking with M-Pesa… Tickets will not be issued until Safaricom reports a successful payment.');
      }
    } catch (err) {
      if (holdHasEnded()) {
        leaveWaitingAfterHoldExpired();
      } else {
        setError(apiErrorMessage(err, 'Could not check payment status with M-Pesa. Please wait and try again.'));
      }
    } finally {
      pollInFlightRef.current = false;
    }
  }, [transactionId, onPaymentComplete, onHoldExpired, expiresAt]);

  useEffect(() => {
    if (step === 'processing' && transactionId) {
      const firstPoll = setTimeout(pollPaymentStatus, 8000);
      pollIntervalRef.current = setInterval(pollPaymentStatus, 5000);
      return () => {
        clearTimeout(firstPoll);
        clearInterval(pollIntervalRef.current);
      };
    }
  }, [step, transactionId, pollPaymentStatus]);

  const updateConcessionQty = (item, delta) => {
    setSelectedConcessions((prev) => {
      const current = prev[item.id]?.quantity || 0;
      const nextQty = Math.max(0, Math.min(10, current + delta));
      if (nextQty === 0) {
        const { [item.id]: _removed, ...rest } = prev;
        return rest;
      }
      return {
        ...prev,
        [item.id]: {
          id: item.id,
          name: item.name,
          catalogPrice: item.price,
          quantity: nextQty,
        },
      };
    });
  };

  const saveConcessionCart = async () => {
    if (!reservationId) {
      throw new Error('Seat reservation is missing. Please select seats again.');
    }
    const items = getConcessionList().map((item) => ({
      id: item.id,
      quantity: item.quantity,
    }));
    const response = await axios.post(`${API_URL}/api/public/cinema/concessions`, {
      reservation_id: reservationId,
      items,
    });
    if (!Array.isArray(response.data.concessions)) {
      throw new Error('Cinema did not return priced snacks. Please try again.');
    }
    setPricedConcessions(response.data.concessions);
    return response.data;
  };

  const goToPaymentForm = async () => {
    if (countdown <= 0) {
      setError('Seat hold expired. Please select seats again.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await saveConcessionCart();
      setStep('form');
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not save snacks. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (countdown <= 0) {
      setError('Seat hold expired. Please select seats again.');
      return;
    }
    if (!reservationId) {
      setError('Seat reservation is missing. Please select seats again.');
      return;
    }
    if (!isKenyanMobileNumber(formData.phone)) {
      setError('Enter a valid Kenyan mobile number for Safaricom, Airtel, Telkom, or Equitel (07xx or 01xx)');
      return;
    }
    if (String(formData.name).trim().length < 2) {
      setError('Enter the name on the booking');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await saveConcessionCart();
      const response = await axios.post(`${API_URL}/api/public/mpesa/initiate`, {
        phone_number: toKenyanApiPhone(formData.phone) || formData.phone,
        reservation_id: reservationId,
        customer_name: formData.name,
        customer_email: formData.email || '',
      });
      if (!response.data.transaction_id) {
        throw new Error('M-Pesa did not return a transaction id.');
      }
      if (response.data.amount == null) {
        throw new Error('M-Pesa did not return the amount to charge.');
      }
      setTransactionId(response.data.transaction_id);
      setChargedAmount(response.data.amount);
      setServerTicketTotal(response.data.ticket_total);
      setPricedConcessions(Array.isArray(response.data.concessions) ? response.data.concessions : []);
      setWaitingHint(null);
      setStep('processing');
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not start M-Pesa payment. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  const formatCountdown = () => {
    const mins = Math.floor(countdown / 60);
    const secs = countdown % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const HoldBanner = () => (
    <div
      className={`mx-4 mt-3 p-3 rounded-lg border ${countdown <= 60 ? 'bg-red-50 border-red-200' : 'bg-emerald-50 border-emerald-200'}`}
      role="status"
      aria-live={countdown <= 60 ? "assertive" : "polite"}
      aria-atomic="true"
    >
      <p className={`text-base font-bold ${countdown <= 60 ? 'text-[#EC1A23]' : 'text-[#1B6B4A]'}`}>
        Seats locked:{' '}
        <span className={`cb-timer-blink ${countdown <= 60 ? 'text-[#EC1A23]' : 'text-[#146C43]'}`}>
          {formatCountdown()}
        </span>
      </p>
      <p className={`text-xs mt-0.5 font-medium ${countdown <= 60 ? 'text-[#B91C1C]' : 'text-[#1B6B4A]'}`}>
        Complete concessions and M-Pesa payment before the timer ends
      </p>
    </div>
  );

  // Success Screen
  if (step === 'success' && booking) {
    return (
      <div className="chatbot-app absolute inset-0 z-30 bg-white flex flex-col" data-testid="booking-success-view">
        <div className="bg-white w-full h-full min-h-0 flex flex-col overflow-hidden animate-fade-in">
          <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 p-5 text-white text-center shrink-0">
            <div className="w-16 h-16 bg-white/20 rounded-full mx-auto mb-4 flex items-center justify-center">
              <CheckCircle className="h-10 w-10" />
            </div>
            <h2 className="cb-page-title text-xl">Booking Confirmed!</h2>
            <p className="text-emerald-100 text-sm mt-1">Your tickets are ready</p>
          </div>
          
          <div className="p-4 space-y-4 flex-1 min-h-0 overflow-y-auto">
            {(booking.tickets || []).length > 0 ? (
              booking.tickets.map((ticket) => (
                <div key={ticket.ticket_no} className="border border-[#E8D9C8] rounded-lg p-3 bg-[#F7F0E8]">
                  <div className="flex gap-3">
                    <img
                      src={`${API_URL}/api/public/ticket-qr/${booking.reference_id}/${encodeURIComponent(ticket.seat)}?t=${encodeURIComponent(booking.ticket_access_token)}`}
                      alt={`QR ${ticket.seat}`}
                      className="w-24 h-24 bg-white rounded"
                    />
                    <div className="flex-1 text-left text-sm">
                      <p className="text-xs text-[#8B6914] uppercase tracking-wide">Admit one</p>
                      <p className="font-bold text-[#561D1A]">{booking.movie_title}</p>
                      <p className="text-2xl font-bold text-[#EC1A23]">{ticket.seat}</p>
                      <p className="font-mono tracking-widest text-[#561D1A]">{ticket.code}</p>
                      <p className="text-xs text-gray-500">{ticket.ticket_no}</p>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center">
                <div className="inline-block p-3 bg-white border-2 border-gray-200 rounded-lg">
                  <img
                    src={`${API_URL}/api/public/qr/${booking.reference_id}?t=${encodeURIComponent(booking.ticket_access_token)}`}
                    alt="Ticket QR Code"
                    className="w-32 h-32"
                  />
                </div>
              </div>
            )}
            <div className="bg-gray-50 p-4 rounded-lg space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Reference</span>
                <span className="font-mono font-bold text-[#EC1A23]">{booking.reference_id}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Movie</span>
                <span className="font-medium">{booking.movie_title}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Date & Time</span>
                <span className="font-medium">{booking.showtime}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Seats</span>
                <span className="font-medium">{booking.seats.join(', ')}</span>
              </div>
              {booking.mpesa_receipt && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">M-Pesa Receipt</span>
                  <span className="font-mono text-xs">{booking.mpesa_receipt}</span>
                </div>
              )}
              <p className="text-xs text-gray-500 pt-2">Tickets and the tax invoice are also sent by email and WhatsApp when those channels are enabled.</p>
            </div>
            
            {is3D && (
              <div className="flex items-center gap-2 p-3 bg-amber-50 rounded-lg text-sm">
                <Glasses className="h-5 w-5 text-amber-600 glasses-3d-pulse" />
                <span className="text-amber-800">Buy 3D glasses at the cinema counter (not included in this payment)</span>
              </div>
            )}
            
            <button
              onClick={onClose}
              className="w-full py-3 bg-gradient-to-r from-[#EC1A23] to-[#d64548] text-white font-semibold rounded-lg"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Processing Screen
  if (step === 'processing') {
    return (
      <div className="chatbot-app absolute inset-0 z-30 bg-white flex flex-col" data-testid="mpesa-processing-view">
        <div className="bg-white w-full h-full min-h-0 flex flex-col overflow-hidden">
          <div className="p-6 text-center flex-1 min-h-0 overflow-y-auto flex flex-col justify-center">
            <div className="w-20 h-20 mx-auto mb-4 relative">
              <div className="absolute inset-0 border-4 border-gray-200 rounded-full" />
              <div className="absolute inset-0 border-4 border-[#EC1A23] rounded-full animate-spin border-t-transparent" />
              <div className="absolute inset-3 flex items-center justify-center">
                <CreditCard className="h-8 w-8 text-[#EC1A23]" />
              </div>
            </div>
            
            <h2 className="text-xl font-bold text-[#561D1A]">Waiting for M-Pesa</h2>
            <p className="text-gray-500 mt-2">Check your phone and enter your M-Pesa PIN. Tickets are confirmed only after M-Pesa reports a successful payment.</p>
            
            <div className={`mt-4 p-4 rounded-lg ${countdown <= 0 ? 'bg-amber-50' : countdown <= 60 ? 'bg-red-50' : 'bg-emerald-50'}`}>
              <p className={`text-sm ${countdown <= 0 ? 'text-amber-800' : countdown <= 60 ? 'text-red-700' : 'text-emerald-700'}`}>
                {countdown <= 0
                  ? 'Seat hold ended. Checking M-Pesa one last time…'
                  : 'Enter your M-Pesa PIN to complete payment'}
              </p>
              <p className={`text-2xl font-mono font-bold mt-2 ${countdown <= 0 ? 'text-amber-900' : countdown <= 60 ? 'text-red-800' : 'text-emerald-800'}`}>{formatCountdown()}</p>
              <p className={`text-xs ${countdown <= 0 ? 'text-amber-700' : countdown <= 60 ? 'text-red-600' : 'text-emerald-600'}`}>
                {countdown <= 0 ? 'If M-Pesa did not confirm, you will return to seat selection' : 'Seat hold time remaining'}
              </p>
            </div>

            {waitingHint && (
              <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                {waitingHint}
              </div>
            )}
            {error && (
              <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                {error}
              </div>
            )}
            
            <div className="mt-4 text-sm text-gray-500">
              <p>Amount: <span className="font-bold text-[#561D1A]">KES {Number(chargedAmount).toLocaleString()}</span></p>
              <p className="mt-1">Do not close this screen until M-Pesa confirms.</p>
            </div>
            
            <button
              onClick={() => {
                clearInterval(pollIntervalRef.current);
                setWaitingHint(null);
                setStep('form');
                setError('Payment was cancelled before M-Pesa confirmed. Your seats are still held until the timer ends — you can try again.');
              }}
              className="mt-4 text-sm text-gray-500 hover:text-gray-700"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (step === 'concessions') {
    const snackSearch = snackQuery.trim().toLowerCase();
    // A typed query searches the whole menu; the category rail only filters when idle.
    const snackPool = snackSearch
      ? snackItems
      : (selectedSnackCategory === 'all'
        ? snackItems
        : snackItems.filter((item) => item.category_id === selectedSnackCategory));
    const filteredSnacks = snackPool
      .filter((item) => !snackSearch || String(item.name || '').toLowerCase().includes(snackSearch))
      .slice()
      .sort(compareByNameAsc);
    const snackUnitCount = getConcessionList().reduce((sum, item) => sum + Number(item.quantity || 0), 0);
    const categoryTabs = [{ id: 'all', name: 'All' }, ...snackCategories];

    return (
      <div className="chatbot-app absolute inset-0 z-30 bg-[#F7F0E8] flex" data-testid="snacks-view">
        <div className="bg-[#F7F0E8] w-full h-full min-h-0 flex flex-col">
          <div className="bg-[#EC1A23] text-white px-3 py-3 flex items-center justify-between shrink-0">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center gap-1 text-xs font-bold tracking-wide uppercase text-white"
              data-testid="snacks-back-btn"
            >
              <ChevronLeft className="h-4 w-4" />
              Back
            </button>
            <h2 className="cb-page-title text-base sm:text-lg">Snacks</h2>
            <button
              type="button"
              disabled={countdown <= 0}
              onClick={() => goToPaymentForm()}
              className="text-xs font-bold tracking-wide underline underline-offset-2 disabled:opacity-50 text-white min-h-11 px-1"
            >
              Skip
            </button>
          </div>
          <BookingStepper currentStepId="snacks" />

          {error && (
            <div className="mx-3 mt-2 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700 text-sm">
              <AlertCircle className="h-5 w-5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <div className="px-3 py-2 bg-white border-b border-[var(--cb-chip-border)] shrink-0 space-y-2">
            <div
              className={`p-2.5 rounded-lg border ${countdown <= 60 ? 'bg-red-50 border-red-200' : 'bg-emerald-50 border-emerald-200'}`}
              role="status"
              aria-live={countdown <= 60 ? "assertive" : "polite"}
              aria-atomic="true"
            >
              <p className={`text-base sm:text-lg font-bold ${countdown <= 60 ? 'text-[#EC1A23]' : 'text-[#1B6B4A]'}`}>
                Seats locked:{' '}
                <span className={`cb-timer-blink ${countdown <= 60 ? 'text-[#EC1A23]' : 'text-[#146C43]'}`}>
                  {formatCountdown()}
                </span>
              </p>
            </div>
            <p className="text-xs text-[var(--cb-ink-muted)] truncate font-medium">
              {movie.title} · {session.time} · Seats {seats.join(', ')}
            </p>

            {/* Categories: always horizontal, above search */}
            <div className="-mx-3 px-3 overflow-x-auto scrollbar-thin" data-testid="snack-categories">
              <div className="flex gap-2 pb-1 min-w-max">
                {categoryTabs.map((cat) => {
                  const Icon = getConcessionCategoryIcon(cat.name);
                  const active = selectedSnackCategory === cat.id;
                  return (
                    <button
                      key={cat.id}
                      type="button"
                      onClick={() => setSelectedSnackCategory(cat.id)}
                      className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border transition-colors ${
                        active
                          ? 'bg-[#EC1A23] border-[#EC1A23] text-white shadow-sm'
                          : 'bg-[#F7F0E8] border-[var(--cb-chip-border)] text-[var(--cb-ink-secondary)] hover:border-[#EC1A23]'
                      }`}
                    >
                      <Icon className={`h-4 w-4 ${active ? 'text-white' : 'text-[var(--cb-ink-muted)]'}`} />
                      <span className="text-[11px] font-semibold whitespace-nowrap">
                        {cat.name}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--cb-ink-muted)]" />
              <input
                type="text"
                value={snackQuery}
                onChange={(event) => setSnackQuery(event.target.value)}
                placeholder="Search snacks & drinks"
                className="w-full pl-8 pr-8 py-2 text-sm font-medium rounded-lg border border-[var(--cb-chip-border)] bg-[#F7F0E8] text-[var(--cb-ink)] placeholder:text-[var(--cb-ink-muted)] focus:outline-none focus:border-[#EC1A23]"
                data-testid="snack-search"
              />
              {snackQuery && (
                <button
                  type="button"
                  onClick={() => setSnackQuery('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-[var(--cb-ink-muted)]"
                  aria-label="Clear snack search"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto p-2 sm:p-3 pb-4">
              {snacksLoading && (
                <div className="flex items-center justify-center py-16 text-[var(--cb-ink-muted)] gap-2">
                  <Loader2 className="h-5 w-5 animate-spin" />
                  Loading snacks...
                </div>
              )}

              {!snacksLoading && !snacksAvailable && (
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                  Concession menu is unavailable right now. Tap SKIP or Proceed to continue to M-Pesa — your seats stay locked.
                </div>
              )}

              {!snacksLoading && snacksAvailable && filteredSnacks.length === 0 && (
                <div className="py-16 text-center text-sm text-[var(--cb-ink-muted)]">
                  {snackSearch
                    ? `No snacks match "${snackQuery.trim()}"`
                    : 'No items in this category.'}
                </div>
              )}

              {!snacksLoading && snacksAvailable && (
                <div className="grid grid-cols-2 gap-3">
                  {filteredSnacks.map((item) => {
                    const qty = selectedConcessions[item.id]?.quantity || 0;
                    return (
                      <div
                        key={item.id}
                        className="bg-white border border-[var(--cb-chip-border)] rounded-xl overflow-hidden flex flex-col shadow-sm"
                      >
                        <div className="aspect-[4/3] bg-[#F7F0E8] flex items-center justify-center overflow-hidden p-2">
                          {item.image_url ? (
                            <img
                              src={item.image_url}
                              alt={item.name}
                              className="w-full h-full object-contain"
                              loading="lazy"
                            />
                          ) : (
                            <Coffee className="h-8 w-8 text-[var(--cb-ink-muted)] opacity-40" />
                          )}
                        </div>
                        <div className="p-2.5 flex flex-col gap-1.5 flex-1">
                          <p className="text-xs font-bold text-[var(--cb-ink)] leading-snug line-clamp-2 min-h-[2.4em]">
                            {item.name}
                          </p>
                          <p className="text-sm font-bold text-[#EC1A23]">
                            KSh {Number(item.price || 0).toLocaleString()}
                          </p>
                          <div className="mt-auto flex items-center justify-center gap-3">
                            <button
                              type="button"
                              onClick={() => updateConcessionQty(item, -1)}
                              disabled={qty === 0}
                              className="w-11 h-11 shrink-0 rounded-lg border border-[#EC1A23] bg-white text-[#EC1A23] text-xl font-bold leading-none flex items-center justify-center disabled:opacity-35 disabled:cursor-not-allowed hover:bg-[#EC1A23]/10"
                              aria-label={`Decrease ${item.name}`}
                            >
                              −
                            </button>
                            <span className="min-w-[1.75rem] text-center text-base font-bold text-[var(--cb-ink)] tabular-nums">{qty}</span>
                            <button
                              type="button"
                              onClick={() => updateConcessionQty(item, 1)}
                              className="w-11 h-11 shrink-0 rounded-lg bg-[#EC1A23] border border-[#EC1A23] text-white text-xl font-bold leading-none flex items-center justify-center hover:bg-[#c4161e]"
                              aria-label={`Increase ${item.name}`}
                            >
                              +
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
          </div>

          <div className="shrink-0 border-t border-[var(--cb-chip-border)] bg-white">
            <div className="px-4 py-3 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-[var(--cb-ink-secondary)] truncate">
                  {snackUnitCount} snack item{snackUnitCount === 1 ? '' : 's'}
                </p>
                <p
                  className={`text-xs font-bold tabular-nums ${countdown <= 60 ? 'text-[#EC1A23]' : 'text-[#1B6B4A]'}`}
                  aria-hidden="true"
                >
                  Seat hold {formatCountdown()}
                </p>
              </div>
              <span className="text-lg font-bold text-[#EC1A23] shrink-0">
                KSh {getConcessionTotal().toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            </div>
            <button
              type="button"
              disabled={countdown <= 0}
              onClick={() => goToPaymentForm()}
              className={`w-full py-4 min-h-12 text-white font-bold tracking-wide disabled:opacity-50 rounded-lg ${
                countdown <= 60 ? 'bg-[#B91C1C]' : 'bg-[#EC1A23]'
              }`}
            >
              {loading
                ? "Saving snacks..."
                : countdown <= 0
                  ? "Seat hold expired"
                  : countdown <= 60
                    ? `Pay now · ${formatCountdown()} left`
                    : "Proceed to payment"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Form Screen
  return (
    <div className="chatbot-app absolute inset-0 z-30 bg-white flex flex-col" data-testid="booking-form-view">
      <div className="bg-white w-full h-full min-h-0 flex flex-col overflow-hidden">
        <div className="bg-gradient-to-r from-[#EC1A23] to-[#561D1A] p-4 text-white flex items-center justify-between shrink-0">
          <div>
            <h2 className="cb-page-title text-base">Complete Booking</h2>
            <p className="text-xs text-white/70">{movie.title}</p>
          </div>
          <button
            type="button"
            onClick={() => {
              setError(null);
              setStep("concessions");
            }}
            className="p-2 hover:bg-white/10 rounded-full"
            aria-label="Back to snacks"
            data-testid="booking-form-back-btn"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
        </div>
        <BookingStepper currentStepId="pay" />
        <div className="shrink-0">
          <HoldBanner />
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4 flex-1 min-h-0 overflow-y-auto">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700 text-sm">
              <AlertCircle className="h-5 w-5" />
              <span>{error}</span>
            </div>
          )}
          
          {/* Session Info */}
          <div className="bg-gray-50 p-3 rounded-lg">
            <div className="flex items-center gap-3">
              <Calendar className="h-5 w-5 text-[#EC1A23]" />
              <div>
                <p className="text-sm font-medium">{formatSessionDate(session.date)} • {session.time}</p>
                <p className="text-xs text-gray-500">{session.screen}</p>
              </div>
            </div>
            <div className="mt-2 flex items-center gap-3">
              <Ticket className="h-5 w-5 text-[#EC1A23]" />
              <div>
                <p className="text-sm font-medium">Seats: {seats.join(', ')}</p>
                <p className="text-xs text-gray-500">{seats.length} ticket(s)</p>
              </div>
            </div>
            {(pricedConcessions.length > 0 ? pricedConcessions : getConcessionList()).length > 0 && (
              <div className="mt-2 flex items-center gap-3">
                <Coffee className="h-5 w-5 text-[#EC1A23]" />
                <div>
                  <p className="text-sm font-medium">
                    {(pricedConcessions.length > 0 ? pricedConcessions : getConcessionList())
                      .map((c) => `${c.quantity}× ${c.name}`)
                      .join(', ')}
                  </p>
                  <button type="button" className="text-xs text-[#EC1A23]" onClick={() => setStep('concessions')}>
                    Edit concessions
                  </button>
                </div>
              </div>
            )}
          </div>
          
          {/* 3D Notice */}
          {is3D && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-2">
              <Glasses className="h-5 w-5 text-amber-600 glasses-3d-pulse" />
              <div>
                <p className="text-sm font-medium text-amber-800">3D Movie</p>
                <p className="text-xs text-amber-600">Buy 3D glasses at the cinema counter — not charged online</p>
              </div>
            </div>
          )}
          
          {/* Form Fields */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Your Name *</label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#EC1A23]/20 focus:border-[#EC1A23] outline-none"
              placeholder="John Doe"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">M-Pesa Phone Number *</label>
            <div className="flex">
              <span className="inline-flex items-center px-3 bg-gray-100 border border-r-0 border-gray-300 rounded-l-lg text-gray-600 text-sm font-semibold">
                +254
              </span>
              <input
                type="tel"
                inputMode="numeric"
                autoComplete="tel-national"
                required
                value={visitorPhoneNsn(formData.phone)}
                onChange={(e) => {
                  const nsn = sanitizeKenyanPhoneInput(e.target.value);
                  setFormData({
                    ...formData,
                    phone: nsn ? formatPhoneForPayment(`254${nsn}`) : "",
                  });
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-r-lg focus:ring-2 focus:ring-[#EC1A23]/20 focus:border-[#EC1A23] outline-none"
                placeholder="712345678"
                maxLength={9}
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">Enter the 9 digits after +254. Safaricom, Airtel, Telkom, or Equitel — you'll receive the M-Pesa prompt on this number</p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email (optional)</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#EC1A23]/20 focus:border-[#EC1A23] outline-none"
              placeholder="john@example.com"
            />
          </div>
          
          {/* Total — display only; M-Pesa charges the server-calculated amount */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="flex justify-between text-sm mb-2">
              <span className="text-gray-600">Tickets ({seats.length})</span>
              <span>KES {Number(serverTicketTotal != null ? serverTicketTotal : (calculateTotal() - getConcessionTotal())).toLocaleString()}</span>
            </div>
            {pricedConcessions.map((item) => (
              <div key={item.id} className="flex justify-between text-sm mb-1">
                <span className="text-gray-600">{item.quantity}× {item.name}</span>
                <span>KES {Number(item.line_total).toLocaleString()}</span>
              </div>
            ))}
            <div className="border-t pt-2 flex justify-between font-bold">
              <span>Total</span>
              <span className="text-[#EC1A23]">
                KES {Number(chargedAmount != null ? chargedAmount : (calculateTotal() - getConcessionTotal() + serverConcessionTotal)).toLocaleString()}
              </span>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Snack and ticket prices come from the cinema. You cannot change price or ticket quantity here.
            </p>
          </div>
          
          <button
            type="submit"
            disabled={loading || countdown <= 0 || !formData.name || !formData.phone}
            className="w-full py-3 bg-gradient-to-r from-[#EC1A23] to-[#d64548] text-white font-semibold rounded-lg disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <CreditCard className="h-5 w-5" />
                Pay with M-Pesa
              </>
            )}
          </button>
          
          <p className="text-xs text-center text-gray-500">
            Secure payment powered by Safaricom M-Pesa
          </p>
        </form>
      </div>
    </div>
  );
};

export default function ChatbotPage() {
  const [accepted, setAccepted] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState("");
  const [activeView, setActiveView] = useState("chat");
  const [stores, setStores] = useState([]);
  const [services, setServices] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [movies, setMovies] = useState([]);
  const [moviesLoading, setMoviesLoading] = useState(false);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [sessionsSource, setSessionsSource] = useState("vista");
  const [selectedMovie, setSelectedMovie] = useState(null);
  const [selectedStore, setSelectedStore] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);
  const [movieSessions, setMovieSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [seatPlan, setSeatPlan] = useState(null);
  const [selectedSeats, setSelectedSeats] = useState([]);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [seatReservation, setSeatReservation] = useState(null);
  const [lockingSeats, setLockingSeats] = useState(false);
  const [seatLockError, setSeatLockError] = useState(null);
  const [selectedShowDate, setSelectedShowDate] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [directoryFloor, setDirectoryFloor] = useState("all");
  const [isOnline, setIsOnline] = useState(true);
  const [suggestions, setSuggestions] = useState([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [allData, setAllData] = useState({ stores: [], services: [], restaurants: [], movies: [] });
  
  // Visitor registration state
  const [showRegistration, setShowRegistration] = useState(false);
  const [visitorAuthMode, setVisitorAuthMode] = useState("register");
  const [showOTPModal, setShowOTPModal] = useState(false);
  const [visitorData, setVisitorData] = useState({ name: '', email: '', phone: '' });
  const [visitorAuthenticated, setVisitorAuthenticated] = useState(false);
  const [registrationLoading, setRegistrationLoading] = useState(false);
  const [otpValue, setOtpValue] = useState('');
  const [otpError, setOtpError] = useState('');
  const [resendDisabled, setResendDisabled] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(0);
  const [pendingAuthAction, setPendingAuthAction] = useState(null);
  const [marketingConsent, setMarketingConsent] = useState({
    email: false,
    sms: false,
    whatsapp: false,
  });
  const [showMarketingPrefs, setShowMarketingPrefs] = useState(false);
  const [marketingPrefsLoading, setMarketingPrefsLoading] = useState(false);
  const [inputPlaceholder, setInputPlaceholder] = useState(() => pickSampleQuestion(""));
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const suggestionsTimeoutRef = useRef(null);
  const lastActivityRef = useRef(Date.now());
  const idleNoticeActiveRef = useRef(false);
  const awaitingMpesaRef = useRef(false);
  const seatReservationRef = useRef(null);
  const [idleNoticeSeconds, setIdleNoticeSeconds] = useState(null);
  const embedMode = isChatEmbed();

  const minimiseWidget = () => {
    if (window.parent && window.parent !== window) {
      window.parent.postMessage({ source: "westgate-chat", event: "close" }, "*");
    }
  };

  useEffect(() => {
    if (!embedMode) {
      return undefined;
    }
    const root = document.getElementById("root");
    const previousHtmlHeight = document.documentElement.style.height;
    const previousBodyHeight = document.body.style.height;
    const previousBodyOverflow = document.body.style.overflow;
    const previousRootHeight = root ? root.style.height : "";
    document.documentElement.classList.add("wg-chat-embed");
    document.documentElement.style.height = "100%";
    document.body.style.height = "100%";
    document.body.style.overflow = "hidden";
    if (root) {
      root.style.height = "100%";
    }
    if (window.parent && window.parent !== window) {
      window.parent.postMessage({ source: "westgate-chat", event: "ready" }, "*");
    }
    return () => {
      document.documentElement.classList.remove("wg-chat-embed");
      document.documentElement.style.height = previousHtmlHeight;
      document.body.style.height = previousBodyHeight;
      document.body.style.overflow = previousBodyOverflow;
      if (root) {
        root.style.height = previousRootHeight;
      }
    };
  }, [embedMode]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, loading]);

  useEffect(() => {
    if (!accepted || activeView !== "chat" || inputValue.trim()) {
      return undefined;
    }
    const rotate = () => {
      setInputPlaceholder((current) => pickSampleQuestion(current));
    };
    // Slow rotation so visitors can read the suggestion in the corner widget
    const interval = setInterval(rotate, 14000);
    return () => clearInterval(interval);
  }, [accepted, activeView, inputValue]);

  useEffect(() => {
    if (!showRegistration && !showOTPModal) {
      return undefined;
    }
    const onKeyDown = (event) => {
      if (event.key !== "Escape") return;
      if (showOTPModal) {
        setShowOTPModal(false);
        setOtpValue("");
        setOtpError("");
        return;
      }
      setShowRegistration(false);
      setPendingAuthAction(null);
      setOtpError("");
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [showRegistration, showOTPModal]);

  useEffect(() => {
    // Legacy JWT visitor tokens are no longer used (HttpOnly cookie sessions)
    localStorage.removeItem("chatbot_visitor_token");
    const hasAccepted = localStorage.getItem("chatbot_terms_accepted");
    const savedSessionId = localStorage.getItem("chatbot_session_id");
    const savedName = localStorage.getItem("chatbot_visitor_name") || "";
    const savedEmail = localStorage.getItem("chatbot_visitor_email") || "";
    const savedPhone = localStorage.getItem("chatbot_visitor_phone") || "";

    if (savedName || savedEmail || savedPhone) {
      setVisitorData({
        name: savedName,
        email: savedEmail,
        phone: toKenyanApiPhone(savedPhone) || savedPhone,
      });
    }

    // Widget stays open across visits: guests can browse; signed-in visitors restore session.
    if (hasAccepted === "true") {
      setAccepted(true);
      setSessionId(savedSessionId || generateUUID());
      addWelcomeMessage();
      fetchAllData();
      const savedVisitorId = localStorage.getItem("chatbot_visitor_id");
      if (savedVisitorId || savedPhone) {
        axios
          .get(`${API_URL}/api/public/visitor/me`, { withCredentials: true })
          .then((response) => {
            const profile = {
              name: response.data?.name || savedName,
              email: response.data?.email || savedEmail,
              phone: toKenyanApiPhone(response.data?.phone || savedPhone) || response.data?.phone || savedPhone,
            };
            setVisitorData(profile);
            setVisitorAuthenticated(true);
            if (profile.name) localStorage.setItem("chatbot_visitor_name", profile.name);
            if (profile.email) localStorage.setItem("chatbot_visitor_email", profile.email);
            if (profile.phone) localStorage.setItem("chatbot_visitor_phone", profile.phone);
          })
          .catch((error) => {
            console.error("Failed to load visitor profile:", error);
            localStorage.removeItem("chatbot_visitor_token");
            localStorage.removeItem("chatbot_visitor_id");
            setVisitorAuthenticated(false);
          });
      }
    }
    
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Smart suggestions
  useEffect(() => {
    if (suggestionsTimeoutRef.current) {
      clearTimeout(suggestionsTimeoutRef.current);
    }
    
    if (inputValue.length < 2) {
      setSuggestions([]);
      return;
    }
    
    setSuggestionsLoading(true);
    suggestionsTimeoutRef.current = setTimeout(() => {
      const query = inputValue.toLowerCase();
      const results = [];
      
      allData.stores.forEach(store => {
        if (store.name?.toLowerCase().includes(query) || store.category?.toLowerCase().includes(query)) {
          results.push({ ...store, type: 'store' });
        }
      });

      allData.services.forEach(service => {
        if (service.name?.toLowerCase().includes(query) || service.category?.toLowerCase().includes(query)) {
          results.push({ ...service, type: 'service' });
        }
      });
      
      allData.restaurants.forEach(restaurant => {
        if (restaurant.name?.toLowerCase().includes(query) || restaurant.cuisine_type?.toLowerCase().includes(query)) {
          results.push({ ...restaurant, type: 'restaurant' });
        }
      });
      
      allData.movies.forEach(movie => {
        if (movie.title?.toLowerCase().includes(query) || movie.genre?.toLowerCase().includes(query)) {
          results.push({ ...movie, type: 'movie' });
        }
      });
      
      setSuggestions(results.slice(0, 5));
      setSuggestionsLoading(false);
    }, 300);
    
    return () => {
      if (suggestionsTimeoutRef.current) {
        clearTimeout(suggestionsTimeoutRef.current);
      }
    };
  }, [inputValue, allData]);

  const fetchAllData = async () => {
    try {
      const [storesRes, servicesRes, restaurantsRes, moviesRes] = await Promise.all([
        axios.get(`${API_URL}/api/public/stores`),
        axios.get(`${API_URL}/api/public/services`),
        axios.get(`${API_URL}/api/public/restaurants`),
        axios.get(`${API_URL}/api/public/movies`)
      ]);
      setAllData({
        stores: storesRes.data || [],
        services: servicesRes.data || [],
        restaurants: restaurantsRes.data || [],
        movies: moviesRes.data || []
      });
    } catch (error) {
      console.error("Failed to fetch data:", error);
    }
  };

  const addWelcomeMessage = () => {
    setMessages([{
      id: "welcome",
      role: "assistant",
      content: "Welcome to Westgate Shopping Mall. I'm an AI assistant — how may I help you today?\n\nI can help with stores, restaurants, movie showtimes & tickets, mall services, and opening hours.",
      timestamp: new Date().toISOString()
    }]);
  };

  const openVisitorRegister = () => {
    setVisitorAuthMode("register");
    setOtpError("");
    setShowOTPModal(false);
    setShowRegistration(true);
  };

  const openVisitorLogin = () => {
    setVisitorAuthMode("login");
    setOtpError("");
    setShowOTPModal(false);
    setShowRegistration(true);
  };

  const enterChatAsGuest = () => {
    localStorage.setItem("chatbot_terms_accepted", "true");
    const newSessionId = generateUUID();
    localStorage.setItem("chatbot_session_id", newSessionId);
    setSessionId(newSessionId);
    setAccepted(true);
    setShowRegistration(false);
    setShowOTPModal(false);
    setShowMarketingPrefs(false);
    setPendingAuthAction(null);
    addWelcomeMessage();
    fetchAllData();
  };

  const handleAcceptTerms = () => {
    // Corner-widget pattern: get into chat fast; booking still requires Sign in.
    enterChatAsGuest();
  };

  const handleVisitorRegistration = async (e) => {
    e.preventDefault();
    if (!isKenyanMobileNumber(visitorData.phone)) {
      setOtpError("Enter a valid Kenyan mobile after +254 (e.g. 712345678)");
      return;
    }
    setRegistrationLoading(true);
    setOtpError('');
    const apiPhone = toKenyanApiPhone(visitorData.phone);
    
    try {
      // Service policies were accepted on Start chatting; marketing is asked after OTP.
      const response = await axios.post(`${API_URL}/api/public/visitor/register`, {
        name: visitorData.name,
        email: visitorData.email,
        phone: apiPhone,
        terms_accepted: true,
        privacy_accepted: true,
        cookies_accepted: true,
        marketing_email: false,
        marketing_sms: false,
        marketing_whatsapp: false,
      });
      setVisitorData((prev) => ({ ...prev, phone: apiPhone }));
      
      if (response.data.requires_otp) {
        setShowOTPModal(true);
        startResendCountdown();
      } else {
        setOtpError("Verification code is required. Please try again.");
      }
    } catch (error) {
      console.error("Registration error:", error);
      setOtpError(apiErrorMessage(error, "Registration failed. Please try again."));
    } finally {
      setRegistrationLoading(false);
    }
  };

  const submitMarketingPreferences = async (skip) => {
    setMarketingPrefsLoading(true);
    try {
      if (skip) {
        setShowMarketingPrefs(false);
        setMarketingConsent({ email: false, sms: false, whatsapp: false });
        return;
      }
      const response = await axios.post(
        `${API_URL}/api/public/visitor/marketing-preferences`,
        {
          marketing_email: marketingConsent.email,
          marketing_sms: marketingConsent.sms,
          marketing_whatsapp: marketingConsent.whatsapp,
        },
        { withCredentials: true }
      );
      setShowMarketingPrefs(false);
      if (response.data?.marketing_opt_in_pending) {
        addBotMessage(
          "Thanks — check your email for a short opt-in link. After you confirm, you'll get a welcome note and occasional mall deals via Mailchimp."
        );
      }
    } catch (error) {
      console.error("Marketing preferences error:", error);
      setOtpError(apiErrorMessage(error, "Could not save update preferences. You can continue chatting."));
      setShowMarketingPrefs(false);
    } finally {
      setMarketingPrefsLoading(false);
    }
  };

  const handleVisitorLogin = async (e) => {
    e.preventDefault();
    if (!isKenyanMobileNumber(visitorData.phone)) {
      setOtpError("Enter a valid Kenyan mobile after +254 (e.g. 712345678)");
      return;
    }
    setRegistrationLoading(true);
    setOtpError("");
    const apiPhone = toKenyanApiPhone(visitorData.phone);
    try {
      const response = await axios.post(`${API_URL}/api/public/visitor/login`, {
        phone: apiPhone,
      });
      setVisitorData((prev) => ({ ...prev, phone: apiPhone }));
      if (!response.data.requires_otp) {
        setOtpError("Verification code is required. Please try again.");
        return;
      }
      setShowOTPModal(true);
      startResendCountdown();
    } catch (error) {
      console.error("Visitor login error:", error);
      setOtpError(apiErrorMessage(error, "Could not find this phone number. Create an account to continue."));
    } finally {
      setRegistrationLoading(false);
    }
  };

  const handleOTPVerification = async (e) => {
    e.preventDefault();
    setRegistrationLoading(true);
    setOtpError('');
    const apiPhone = toKenyanApiPhone(visitorData.phone);
    
    try {
      const response = await axios.post(`${API_URL}/api/public/visitor/verify-otp`, {
        phone: apiPhone,
        otp: otpValue
      });
      
      if (response.data.success) {
        if (!response.data.session_established && !response.data.visitor_id) {
          setOtpError("Verification did not start a session. Please request a new code.");
          return;
        }
        const profile = response.data.visitor
          ? {
              name: response.data.visitor.name || visitorData.name,
              email: response.data.visitor.email || visitorData.email,
              phone: toKenyanApiPhone(response.data.visitor.phone || apiPhone),
            }
          : { ...visitorData, phone: apiPhone };
        setVisitorData(profile);
        const cameFromRegister = visitorAuthMode === "register";
        completeRegistration(response.data.visitor_id, profile);
        if (cameFromRegister) {
          setMarketingConsent({ email: false, sms: false, whatsapp: false });
          setShowMarketingPrefs(true);
        }
      }
    } catch (error) {
      console.error("OTP verification error:", error);
      setOtpError(apiErrorMessage(error, "Invalid OTP. Please try again."));
    } finally {
      setRegistrationLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (resendDisabled) return;
    const apiPhone = toKenyanApiPhone(visitorData.phone);
    
    try {
      await axios.post(`${API_URL}/api/public/visitor/resend-otp`, {
        phone: apiPhone
      });
      startResendCountdown();
      setOtpError('');
    } catch (error) {
      console.error("Resend OTP error:", error);
      setOtpError(apiErrorMessage(error, "Failed to resend OTP. Please try again."));
    }
  };

  const startResendCountdown = () => {
    setResendDisabled(true);
    setResendCountdown(60);
    const interval = setInterval(() => {
      setResendCountdown(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          setResendDisabled(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const completeRegistration = (visitorId, profile) => {
    const saved = profile || visitorData;
    const wasAlreadyInChat = accepted;
    const newSessionId = wasAlreadyInChat ? (sessionId || generateUUID()) : generateUUID();
    localStorage.setItem("chatbot_terms_accepted", "true");
    localStorage.setItem("chatbot_session_id", newSessionId);
    localStorage.setItem("chatbot_visitor_id", visitorId);
    localStorage.removeItem("chatbot_visitor_token");
    localStorage.setItem("chatbot_visitor_name", saved.name || "");
    localStorage.setItem("chatbot_visitor_email", saved.email || "");
    localStorage.setItem("chatbot_visitor_phone", saved.phone || "");
    setVisitorData(saved);
    setVisitorAuthenticated(true);
    setSessionId(newSessionId);
    setShowRegistration(false);
    setShowOTPModal(false);
    setAccepted(true);
    if (!wasAlreadyInChat) {
      addWelcomeMessage();
      fetchAllData();
    }
    if (pendingAuthAction === "tableBooking" && selectedRestaurant) {
      setActiveView("tableBooking");
    }
    setPendingAuthAction(null);
  };

  const fetchStores = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/public/stores`);
      setStores(response.data);
    } catch (error) {
      console.error("Failed to fetch stores:", error);
    }
  };

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/public/services`);
      setServices(response.data);
    } catch (error) {
      console.error("Failed to fetch services:", error);
    }
  };

  const fetchRestaurants = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/public/restaurants`);
      setRestaurants(response.data);
    } catch (error) {
      console.error("Failed to fetch restaurants:", error);
    }
  };

  const fetchMovies = async () => {
    if (allData.movies?.length) {
      setMovies(allData.movies);
    }
    setMoviesLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/public/movies`);
      setMovies(response.data);
    } catch (error) {
      console.error("Failed to fetch movies:", error);
    } finally {
      setMoviesLoading(false);
    }
  };

  const fetchMovieSessions = async (filmId) => {
    setSessionsLoading(true);
    setMovieSessions([]);
    setSessionsSource("vista");
    try {
      const response = await axios.get(`${API_URL}/api/public/cinema/sessions/${filmId}`);
      const sessions = response.data.sessions || [];
      setMovieSessions(sessions);
      setSessionsSource(response.data.source || "vista");
      return sessions;
    } catch (error) {
      console.error("Failed to fetch sessions:", error);
      setMovieSessions([]);
      setSessionsSource("none");
      return [];
    } finally {
      setSessionsLoading(false);
    }
  };

  const fetchSeatPlan = async (sessionId) => {
    try {
      const response = await axios.get(`${API_URL}/api/public/cinema/seats/${sessionId}`);
      setSeatPlan(response.data.seat_plan);
    } catch (error) {
      console.error("Failed to fetch seat plan:", error);
      setSeatPlan(null);
    }
  };

  const addBotMessage = (content, ui = null) => {
    setMessages(prev => [...prev, {
      id: `bot-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      role: "assistant",
      content,
      ui: ui || null,
      timestamp: new Date().toISOString()
    }]);
  };

  const handleQuickAction = async (actionId) => {
    const userMessage = {
      id: `user-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      role: "user",
      content: QUICK_ACTIONS.find(a => a.id === actionId)?.label || actionId,
      timestamp: new Date().toISOString()
    };
    setMessages(prev => [...prev, userMessage]);

    switch (actionId) {
      case "stores":
        fetchStores();
        setDirectoryFloor("all");
        setSearchQuery("");
        setActiveView("stores");
        addBotMessage("Here are all the stores at Westgate Mall! 🛍️\n\nTap any store to see more details.");
        break;
      case "services":
        fetchServices();
        setDirectoryFloor("all");
        setSearchQuery("");
        setActiveView("services");
        addBotMessage("Here are the services available at Westgate Mall.\n\nTap any service to see more details.");
        break;
      case "restaurants":
        fetchRestaurants();
        setDirectoryFloor("all");
        setSearchQuery("");
        setActiveView("restaurants");
        addBotMessage("Discover our dining options! 🍽️\n\nFrom casual cafes to fine dining, we've got something for everyone.");
        break;
      case "movies":
        fetchMovies();
        setActiveView("movies");
        addBotMessage("Here's what's showing at Westgate Cinema! 🎬\n\nTap a movie to see showtimes and book tickets.");
        break;
      case "hours":
        addBotMessage("🕐 *Westgate Mall Hours*\n\n*Mon - Fri:* 10:00 AM - 10:00 PM\n*Saturday:* 10:00 AM - 11:00 PM\n*Sunday:* 11:00 AM - 9:00 PM\n\n*Cinema:* Opens 30 min before first show");
        break;
      default:
        break;
    }
  };

  const handleSuggestionSelect = (item) => {
    setSuggestions([]);
    setInputValue("");
    
    if (item.type === 'store') {
      fetchStores();
      setActiveView("stores");
      setSearchQuery(item.name);
      addBotMessage(`Here's information about ${item.name}! 🏪`);
    } else if (item.type === 'service') {
      fetchServices();
      setActiveView("services");
      setSearchQuery(item.name);
      addBotMessage(`Here's information about ${item.name}!`);
    } else if (item.type === 'restaurant') {
      fetchRestaurants();
      setActiveView("restaurants");
      setSearchQuery(item.name);
      addBotMessage(`Here's information about ${item.name}! 🍽️`);
    } else if (item.type === 'movie') {
      handleSelectMovie(item);
    }
  };

  const sendChatText = async (rawText) => {
    const messageText = String(rawText || "").trim();
    if (!messageText || loading) return;

    setSuggestions([]);
    setInputValue("");
    const userMessage = {
      id: `user-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      role: "user",
      content: messageText,
      timestamp: new Date().toISOString()
    };
    setMessages((prev) => [...prev, userMessage]);
    setLoading(true);

    try {
      const response = await axios.post(`${API_URL}/api/public/chat`, {
        message: messageText,
        session_id: sessionId
      });
      const lineup = response.data?.ui?.type === "movie_lineup" ? response.data.ui : null;
      addBotMessage(response.data.response, lineup);
      if (lineup?.movies?.length) {
        setMovies((prev) => {
          const byId = new Map((prev || []).map((movie) => [movie.id, movie]));
          lineup.movies.forEach((movie) => {
            if (movie?.id) {
              byId.set(movie.id, { ...(byId.get(movie.id) || {}), ...movie });
            }
          });
          return Array.from(byId.values());
        });
      }
    } catch (error) {
      const text = messageText.toLowerCase();
      if (text.includes("store") || text.includes("shop")) {
        handleQuickAction("stores");
      } else if (text.includes("restaurant") || text.includes("food") || text.includes("eat")) {
        handleQuickAction("restaurants");
      } else if (text.includes("movie") || text.includes("cinema") || text.includes("film") || text.includes("bollywood")) {
        handleQuickAction("movies");
      } else if (text.includes("hour") || text.includes("open") || text.includes("close")) {
        handleQuickAction("hours");
      } else {
        addBotMessage("I'm having trouble connecting right now. Please try one of the quick actions below! 🙏");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    await sendChatText(inputValue);
  };

  const handleBackToChat = () => {
    setActiveView("chat");
    setSelectedMovie(null);
    setSelectedStore(null);
    setSelectedService(null);
    setSelectedRestaurant(null);
    setSelectedSession(null);
    setSeatPlan(null);
    setSelectedSeats([]);
    setMovieSessions([]);
    setSearchQuery("");
    setDirectoryFloor("all");
  };

  /** One-step back through browse/booking; only exits to chat from the first list screen. */
  const handleSmartBack = () => {
    if (showPaymentModal) {
      handleClosePaymentModal();
      return;
    }
    if (activeView === "movieSessions") {
      setSelectedMovie(null);
      setMovieSessions([]);
      setSelectedSession(null);
      setActiveView("movies");
      return;
    }
    if (activeView === "seatSelection") {
      setSelectedSession(null);
      setSeatPlan(null);
      setSelectedSeats([]);
      setSeatReservation(null);
      setSeatLockError(null);
      setActiveView("movieSessions");
      return;
    }
    if (activeView === "storeDetail") {
      setSelectedStore(null);
      setActiveView("stores");
      return;
    }
    if (activeView === "serviceDetail") {
      setSelectedService(null);
      setActiveView("services");
      return;
    }
    if (activeView === "restaurantDetail") {
      setSelectedRestaurant(null);
      setActiveView("restaurants");
      return;
    }
    if (activeView === "tableBooking") {
      setActiveView("restaurantDetail");
      return;
    }
    handleBackToChat();
  };

  const cinemaBookingStepId =
    activeView === "movies"
      ? "film"
      : activeView === "movieSessions"
        ? "show"
        : activeView === "seatSelection"
          ? "seats"
          : null;

  seatReservationRef.current = seatReservation;

  const dismissIdleNotice = useCallback(() => {
    idleNoticeActiveRef.current = false;
    setIdleNoticeSeconds(null);
    lastActivityRef.current = Date.now();
  }, []);

  const markUserActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
    if (idleNoticeActiveRef.current) {
      dismissIdleNotice();
    }
  }, [dismissIdleNotice]);

  const handleAwaitingMpesa = useCallback((waiting) => {
    awaitingMpesaRef.current = waiting;
    if (waiting) {
      dismissIdleNotice();
    }
  }, [dismissIdleNotice]);

  const resetSessionFromIdle = useCallback(async () => {
    const reservationId = seatReservationRef.current?.id;
    idleNoticeActiveRef.current = false;
    setIdleNoticeSeconds(null);
    setShowPaymentModal(false);
    setSeatReservation(null);
    setSelectedSeats([]);
    setSelectedMovie(null);
    setSelectedStore(null);
    setSelectedService(null);
    setSelectedRestaurant(null);
    setSelectedSession(null);
    setSeatPlan(null);
    setMovieSessions([]);
    setSelectedShowDate(null);
    setSearchQuery("");
    setDirectoryFloor("all");
    setSeatLockError(null);
    setInputValue("");
    setSuggestions([]);
    setShowRegistration(false);
    setVisitorAuthMode("register");
    setShowOTPModal(false);
    setOtpValue("");
    setOtpError("");
    setRegistrationLoading(false);
    const newSessionId = generateUUID();
    localStorage.setItem("chatbot_session_id", newSessionId);
    setSessionId(newSessionId);
    setActiveView("chat");
    addWelcomeMessage();
    lastActivityRef.current = Date.now();
    if (reservationId) {
      try {
        await axios.post(`${API_URL}/api/public/cinema/release-seats`, {
          reservation_id: reservationId,
        });
      } catch (error) {
        console.error("Failed to release seat hold after idle timeout:", error);
      }
    }
  }, []);

  const handleSelectStore = (store) => {
    setSelectedStore(store);
    setActiveView("storeDetail");
  };

  const handleSelectService = (service) => {
    setSelectedService(service);
    setActiveView("serviceDetail");
  };

  const handleSelectRestaurant = (restaurant) => {
    setSelectedRestaurant(restaurant);
    setActiveView("restaurantDetail");
  };

  const handleBookTable = (restaurant) => {
    setSelectedRestaurant(restaurant);
    if (!visitorAuthenticated) {
      setPendingAuthAction("tableBooking");
      openVisitorLogin();
      addBotMessage("Please verify your phone to book a table.");
      return;
    }
    setActiveView("tableBooking");
  };

  const handleSelectMovie = async (movie, preferredSession = null) => {
    setSelectedMovie(movie);
    setSelectedShowDate(null);
    setSelectedSession(null);
    setSeatPlan(null);
    setSelectedSeats([]);
    setActiveView("movieSessions");
    const sessions = await fetchMovieSessions(movie.id);
    if (preferredSession) {
      const match = (sessions || []).find((session) => {
        if (preferredSession.id && session.id && String(session.id) === String(preferredSession.id)) {
          return true;
        }
        return (
          String(session.date || "") === String(preferredSession.date || "")
          && String(session.time || "") === String(preferredSession.time || "")
        );
      });
      if (match && match.bookable !== false) {
        setSelectedShowDate(match.date || preferredSession.date || null);
        setSelectedSession(match);
        setSelectedSeats([]);
        setSeatReservation(null);
        setSeatLockError(null);
        setActiveView("seatSelection");
        await fetchSeatPlan(match.id);
        addBotMessage(`Next show for ${movie.title}: ${formatLineupDay(match.date)} · ${match.time}. Pick your seats.`);
        return;
      }
    }
    addBotMessage(`Here are the showtimes for ${movie.title}! 🎬\n\nSelect a date and time to continue.`);
  };

  const handleSelectSession = async (session) => {
    if (session.bookable === false) {
      return;
    }
    setSelectedSession(session);
    setSelectedSeats([]);
    setSeatReservation(null);
    setSeatLockError(null);
    setActiveView("seatSelection");
    await fetchSeatPlan(session.id);
  };

  const handleSeatSelect = (seatId) => {
    setSelectedSeats(prev => {
      if (prev.includes(seatId)) {
        return prev.filter(id => id !== seatId);
      }
      if (prev.length >= 10) {
        return prev;
      }
      return [...prev, seatId];
    });
  };

  const releaseSeatHold = async (reservationId) => {
    if (!reservationId) return;
    try {
      await axios.post(`${API_URL}/api/public/cinema/release-seats`, {
        reservation_id: reservationId,
      });
    } catch (error) {
      console.error("Failed to release seat hold:", error);
    }
  };

  const handleProceedToPayment = async () => {
    if (selectedSeats.length === 0 || !selectedSession) return;
    setLockingSeats(true);
    setSeatLockError(null);
    try {
      const response = await axios.post(`${API_URL}/api/public/cinema/reserve-seats`, {
        session_id: selectedSession.id,
        seats: selectedSeats,
        reservation_id: seatReservation?.id || null,
      });
      setSeatReservation({
        id: response.data.reservation_id,
        expires_at: response.data.expires_at,
        hold_minutes: response.data.hold_minutes,
      });
      setShowPaymentModal(true);
    } catch (error) {
      const detail = error.response?.data?.detail || "Failed to lock seats. Please try again.";
      setSeatLockError(detail);
      await fetchSeatPlan(selectedSession.id);
    } finally {
      setLockingSeats(false);
    }
  };

  const handleClosePaymentModal = async () => {
    setShowPaymentModal(false);
    if (seatReservation?.id) {
      await releaseSeatHold(seatReservation.id);
      setSeatReservation(null);
      if (selectedSession?.id) {
        await fetchSeatPlan(selectedSession.id);
      }
    }
  };

  const handleHoldExpired = async () => {
    setShowPaymentModal(false);
    setSeatReservation(null);
    setSelectedSeats([]);
    setSeatLockError("Seat hold expired after 7 minutes. Please select seats again.");
    if (selectedSession?.id) {
      await fetchSeatPlan(selectedSession.id);
    }
  };

  const handlePaymentComplete = (booking) => {
    setSeatReservation(null);
    addBotMessage(`🎉 Your booking is confirmed!\n\nReference: ${booking.reference_id}\nMovie: ${booking.movie_title}\nSeats: ${booking.seats.join(', ')}\n\nShow the QR code at the cinema entrance. Enjoy your movie!`);
    handleBackToChat();
  };

  useEffect(() => {
    const activityEvents = ["pointerdown", "keydown", "touchstart", "scroll"];
    activityEvents.forEach((eventName) => {
      window.addEventListener(eventName, markUserActivity, { capture: true, passive: true });
    });
    const idleWatch = setInterval(() => {
      if (awaitingMpesaRef.current) {
        lastActivityRef.current = Date.now();
        return;
      }
      if (idleNoticeActiveRef.current) {
        return;
      }
      const idleFor = Date.now() - lastActivityRef.current;
      if (idleFor >= IDLE_LIMIT_MS) {
        idleNoticeActiveRef.current = true;
        setIdleNoticeSeconds(IDLE_NOTICE_SECONDS);
      }
    }, 1000);
    return () => {
      activityEvents.forEach((eventName) => {
        window.removeEventListener(eventName, markUserActivity, { capture: true });
      });
      clearInterval(idleWatch);
    };
  }, [markUserActivity]);

  useEffect(() => {
    if (idleNoticeSeconds === null) {
      return undefined;
    }
    if (idleNoticeSeconds <= 0) {
      resetSessionFromIdle();
      return undefined;
    }
    const tick = setTimeout(() => {
      setIdleNoticeSeconds((current) => {
        if (current === null) {
          return null;
        }
        return current - 1;
      });
    }, 1000);
    return () => clearTimeout(tick);
  }, [idleNoticeSeconds, resetSessionFromIdle]);

  const matchesDirectoryFloor = (item) =>
    directoryFloor === "all" || normalizeFloorLabel(item?.floor) === directoryFloor;

  const filteredStores = stores.filter((s) => {
    const q = searchQuery.toLowerCase();
    const textMatch =
      !q
      || s.name?.toLowerCase().includes(q)
      || s.category?.toLowerCase().includes(q)
      || normalizeFloorLabel(s.floor).toLowerCase().includes(q);
    return textMatch && matchesDirectoryFloor(s);
  }).sort(compareByNameAsc);

  const filteredServices = services.filter((service) => {
    const q = searchQuery.toLowerCase();
    const textMatch =
      !q
      || service.name?.toLowerCase().includes(q)
      || service.category?.toLowerCase().includes(q)
      || normalizeFloorLabel(service.floor).toLowerCase().includes(q);
    return textMatch && matchesDirectoryFloor(service);
  }).sort(compareByNameAsc);

  const filteredRestaurants = restaurants.filter((r) => {
    const q = searchQuery.toLowerCase();
    const textMatch =
      !q
      || r.name?.toLowerCase().includes(q)
      || r.cuisine_type?.toLowerCase().includes(q)
      || normalizeFloorLabel(r.floor).toLowerCase().includes(q);
    return textMatch && matchesDirectoryFloor(r);
  }).sort(compareByNameAsc);

  const kenyaYmd = (date) =>
    new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Africa/Nairobi',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(date);

  const kenyaTodayYmd = () => kenyaYmd(new Date());

  const kenyaTomorrowYmd = () => kenyaYmd(new Date(Date.now() + 24 * 60 * 60 * 1000));

  const isUpcomingKenyaSession = (session) => {
    const sortKey = parseShowtimeSortKey(session.time);
    if (!session.date || !/^\d{2}:\d{2}$/.test(sortKey)) {
      return false;
    }
    const sessionAt = new Date(`${session.date}T${sortKey}:00+03:00`);
    return sessionAt.getTime() > Date.now();
  };

  const formatDateLabel = (dateStr) => {
    const today = kenyaTodayYmd();
    const tomorrow = kenyaTomorrowYmd();
    if (dateStr === today) return "Today";
    if (dateStr === tomorrow) return "Tomorrow";
    const date = new Date(dateStr + 'T12:00:00+03:00');
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'Africa/Nairobi' });
  };

  const formatDateChip = (dateStr) => {
    const today = kenyaTodayYmd();
    const date = new Date(dateStr + 'T12:00:00+03:00');
    const monthDay = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'Africa/Nairobi' });
    if (dateStr === today) {
      return { primary: 'Today', secondary: monthDay };
    }
    return {
      primary: date.toLocaleDateString('en-US', { weekday: 'short', timeZone: 'Africa/Nairobi' }) + ',',
      secondary: monthDay,
    };
  };

  const parseShowtimeSortKey = (timeStr) => {
    const match = String(timeStr || '').match(/(\d+):(\d+)\s*(AM|PM)/i);
    if (!match) return String(timeStr || '');
    let hour = parseInt(match[1], 10);
    const min = match[2];
    const ap = match[3].toUpperCase();
    if (ap === 'PM' && hour !== 12) hour += 12;
    if (ap === 'AM' && hour === 12) hour = 0;
    return `${String(hour).padStart(2, '0')}:${min}`;
  };

  // Group upcoming Kenya sessions by date; hide times that already started.
  const groupedSessions = movieSessions.filter(isUpcomingKenyaSession).reduce((acc, session) => {
    const date = session.date;
    if (!date) return acc;
    if (!acc[date]) acc[date] = [];

    const isDuplicate = acc[date].some((existing) =>
      (session.id && existing.id === session.id) ||
      (
        existing.time === session.time &&
        existing.screen === session.screen &&
        Boolean(existing.is_3d) === Boolean(session.is_3d)
      )
    );

    if (!isDuplicate) {
      acc[date].push(session);
    }
    return acc;
  }, {});

  // Sort sessions within each date by time
  Object.keys(groupedSessions).forEach((date) => {
    groupedSessions[date].sort((a, b) =>
      parseShowtimeSortKey(a.time).localeCompare(parseShowtimeSortKey(b.time))
    );
  });

  // Get sorted date keys (Today first, Tomorrow second, then chronological)
  const sortedDates = Object.keys(groupedSessions).sort((a, b) => {
    const today = kenyaTodayYmd();
    const tomorrow = kenyaTomorrowYmd();
    
    if (a === today) return -1;
    if (b === today) return 1;
    if (a === tomorrow) return -1;
    if (b === tomorrow) return 1;
    return a.localeCompare(b);
  });

  const sortedDatesKey = sortedDates.join(',');
  useEffect(() => {
    if (sortedDates.length === 0) {
      setSelectedShowDate(null);
      return;
    }
    setSelectedShowDate((prev) => (prev && sortedDates.includes(prev) ? prev : sortedDates[0]));
  }, [sortedDatesKey]);

  const sessionsForSelectedDate = selectedShowDate
    ? (groupedSessions[selectedShowDate] || [])
    : [];

  const idleOverlay = idleNoticeSeconds !== null
    ? <IdleSessionNotice remainingSeconds={idleNoticeSeconds} />
    : null;

  // Auth (login/register) — can open before chat or mid-session for booking gates
  if (showRegistration) {
      return (
        <>
        <div className={embedMode ? "chatbot-app h-full min-h-0 w-full bg-white overflow-y-auto" : "chatbot-app min-h-screen bg-[var(--cb-surface)] flex items-center justify-center p-4"} data-testid="registration-screen">
          <div className={embedMode ? "w-full min-h-full bg-white overflow-hidden flex flex-col" : "w-full max-w-md bg-white shadow-xl rounded-xl overflow-hidden"}>
            <div className={`bg-gradient-to-r from-[#EC1A23] to-[#561D1A] text-white text-center ${embedMode ? "p-4" : "p-6"}`}>
              <div className={`${embedMode ? "w-12 h-12 mb-2" : "w-16 h-16 mb-3"} bg-white/20 mx-auto flex items-center justify-center rounded-full`}>
                {visitorAuthMode === "login"
                  ? <Phone className={embedMode ? "h-6 w-6" : "h-8 w-8"} />
                  : <User className={embedMode ? "h-6 w-6" : "h-8 w-8"} />}
              </div>
              <h1 className={`cb-page-title ${embedMode ? "text-xl" : "text-2xl"}`}>
                {visitorAuthMode === "login" ? "Welcome back" : "Create Account"}
              </h1>
              <p className="text-white/80 text-sm mt-1 font-medium">
                {visitorAuthMode === "login"
                  ? "Enter your registered phone number"
                  : "Sign up to book tickets & tables"}
              </p>
            </div>

            <form
              onSubmit={visitorAuthMode === "login" ? handleVisitorLogin : handleVisitorRegistration}
              className="p-6 space-y-4"
            >
              {otpError && !showOTPModal && (
                <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm">
                  {otpError}
                  {visitorAuthMode === "login" && (
                    <button
                      type="button"
                      onClick={openVisitorRegister}
                      className="block mt-2 font-semibold text-[#EC1A23] underline"
                    >
                      Create an account
                    </button>
                  )}
                </div>
              )}

              {visitorAuthMode === "register" && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                    <input
                      type="text"
                      required
                      value={visitorData.name}
                      onChange={(e) => setVisitorData({...visitorData, name: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#EC1A23] focus:border-[#EC1A23] outline-none"
                      placeholder="Enter your full name"
                      data-testid="visitor-name-input"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                    <input
                      type="email"
                      required
                      value={visitorData.email}
                      onChange={(e) => setVisitorData({...visitorData, email: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#EC1A23] focus:border-[#EC1A23] outline-none"
                      placeholder="Enter your email"
                      data-testid="visitor-email-input"
                    />
                  </div>
                </>
              )}
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                <div className="flex">
                  <span className="inline-flex items-center px-3 bg-gray-100 border border-r-0 border-gray-300 rounded-l-lg text-gray-600 text-sm font-semibold">
                    +254
                  </span>
                  <input
                    type="tel"
                    inputMode="numeric"
                    autoComplete="tel-national"
                    required
                    value={visitorPhoneNsn(visitorData.phone)}
                    onChange={(e) => {
                      const nsn = sanitizeKenyanPhoneInput(e.target.value);
                      setVisitorData({
                        ...visitorData,
                        phone: nsn ? `254${nsn}` : "",
                      });
                    }}
                    className="w-full px-4 py-3 border border-gray-300 rounded-r-lg focus:ring-2 focus:ring-[#EC1A23] focus:border-[#EC1A23] outline-none"
                    placeholder="712345678"
                    maxLength={9}
                    data-testid="visitor-phone-input"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Enter the 9 digits after +254 (do not type 254 again).{" "}
                  {visitorAuthMode === "login"
                    ? "We'll send a code if this number is registered."
                    : "We'll send a verification code to this number."}
                </p>
              </div>

              {visitorAuthMode === "register" && (
                <p className="text-base text-[var(--cb-ink-muted)] leading-relaxed">
                  By creating an account you agree to our{" "}
                  <a href={POLICY_LINKS.terms} target="_blank" rel="noopener noreferrer" className="text-[#EC1A23] underline">Terms</a>
                  {", "}
                  <a href={POLICY_LINKS.privacy} target="_blank" rel="noopener noreferrer" className="text-[#EC1A23] underline">Privacy</a>
                  {" and "}
                  <a href={POLICY_LINKS.cookies} target="_blank" rel="noopener noreferrer" className="text-[#EC1A23] underline">Cookies</a>
                  {" policies. Marketing updates are optional and asked after verification."}
                </p>
              )}

              <button
                type="submit"
                disabled={
                  registrationLoading
                  || !visitorData.phone
                  || (visitorAuthMode === "register" && (!visitorData.name || !visitorData.email))
                }
                className="w-full py-4 bg-gradient-to-r from-[#EC1A23] to-[#561D1A] text-white font-semibold rounded-lg flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid={visitorAuthMode === "login" ? "visitor-login-btn" : "register-btn"}
              >
                {registrationLoading ? (
                  <>
                    <RefreshCw className="h-5 w-5 animate-spin" />
                    {visitorAuthMode === "login" ? "Checking number..." : "Processing..."}
                  </>
                ) : (
                  <>
                    <Send className="h-5 w-5" />
                    Continue
                  </>
                )}
              </button>

              {visitorAuthMode === "login" ? (
                <button
                  type="button"
                  onClick={openVisitorRegister}
                  className="w-full py-2 text-sm text-[#EC1A23] font-semibold"
                  data-testid="switch-to-register"
                >
                  New here? Create an account
                </button>
              ) : (
                <button
                  type="button"
                  onClick={openVisitorLogin}
                  className="w-full py-2 text-sm text-[#EC1A23] font-semibold"
                  data-testid="switch-to-login"
                >
                  Already registered? Sign in with phone
                </button>
              )}

              <button
                type="button"
                onClick={() => {
                  setShowRegistration(false);
                  setShowOTPModal(false);
                  setOtpError("");
                  setPendingAuthAction(null);
                }}
                className="w-full py-2 text-gray-600 text-sm hover:text-gray-800"
              >
                ← Back
              </button>
            </form>
          </div>

          {/* OTP Verification Modal */}
          {showOTPModal && (
            <div className="absolute inset-0 bg-white flex items-center justify-center p-3 z-50" data-testid="otp-view">
              <div className={`w-full max-w-sm bg-white rounded-xl space-y-4 ${embedMode ? "p-4" : "p-6"}`}>
                <div className="text-center">
                  <div className={`${embedMode ? "w-12 h-12 mb-2" : "w-16 h-16 mb-3"} bg-emerald-100 mx-auto flex items-center justify-center rounded-full`}>
                    <Phone className={`${embedMode ? "h-6 w-6" : "h-8 w-8"} text-emerald-600`} />
                  </div>
                  <h2 className={`${embedMode ? "text-lg" : "text-xl"} font-bold text-gray-800`}>Verify Phone</h2>
                  <p className="text-sm text-gray-600 mt-1">
                    Enter the 6-digit code sent to<br />
                    <span className="font-medium">+254 {visitorPhoneNsn(visitorData.phone)}</span>
                  </p>
                </div>

                {otpError && (
                  <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm text-center">
                    {otpError}
                  </div>
                )}

                <form onSubmit={handleOTPVerification}>
                  <input
                    type="text"
                    value={otpValue}
                    onChange={(e) => setOtpValue(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="w-full px-4 py-4 text-center text-2xl font-mono tracking-[0.5em] border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#EC1A23] focus:border-[#EC1A23] outline-none"
                    placeholder="• • • • • •"
                    maxLength={6}
                    autoFocus
                    data-testid="otp-input"
                  />

                  <button
                    type="submit"
                    disabled={registrationLoading || otpValue.length !== 6}
                    className="w-full mt-4 py-3 bg-gradient-to-r from-[#EC1A23] to-[#561D1A] text-white font-semibold rounded-lg flex items-center justify-center gap-2 disabled:opacity-50"
                    data-testid="verify-otp-btn"
                  >
                    {registrationLoading ? (
                      <>
                        <RefreshCw className="h-5 w-5 animate-spin" />
                        Verifying...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="h-5 w-5" />
                        Verify & Continue
                      </>
                    )}
                  </button>
                </form>

                <div className="text-center">
                  <button
                    onClick={handleResendOTP}
                    disabled={resendDisabled}
                    className="text-sm text-[#EC1A23] hover:underline disabled:text-gray-400 disabled:no-underline"
                  >
                    {resendDisabled ? `Resend code in ${resendCountdown}s` : "Didn't receive code? Resend"}
                  </button>
                </div>

                <button
                  type="button"
                  onClick={() => {
                    setShowOTPModal(false);
                    setOtpValue('');
                    setOtpError('');
                  }}
                  className="w-full py-2 text-gray-600 text-sm hover:text-gray-800"
                >
                  ← Back to edit details
                </button>
              </div>
            </div>
          )}
        </div>
        {idleOverlay}
        </>
      );
  }

  // Compact welcome for corner embed widget — chat first, account when needed
  if (!accepted) {
    return (
      <>
      <div className={embedMode ? "chatbot-app h-full min-h-0 w-full bg-white overflow-y-auto" : "chatbot-app min-h-screen bg-[var(--cb-surface)] flex items-center justify-center p-4"} data-testid="terms-screen">
        <div className={embedMode ? "w-full min-h-full bg-white overflow-hidden flex flex-col" : "w-full max-w-md bg-white shadow-xl rounded-xl overflow-hidden"}>
          <div className={`bg-gradient-to-r from-[#EC1A23] to-[#561D1A] text-white text-center ${embedMode ? "p-5" : "p-7"}`}>
            <div className={`${embedMode ? "w-12 h-12 mb-2" : "w-16 h-16 mb-3"} bg-white/20 mx-auto flex items-center justify-center rounded-full`}>
              <MessageSquare className={embedMode ? "h-6 w-6" : "h-8 w-8"} />
            </div>
            <h1 className={`cb-page-title ${embedMode ? "text-xl" : "text-2xl"}`}>Westgate Mall</h1>
            <p className="text-white/90 text-base mt-0.5 font-medium">Customer Support</p>
          </div>

          <div className={`flex-1 flex flex-col ${embedMode ? "p-4 space-y-4" : "p-5 space-y-5"}`}>
            <div className="space-y-2">
              <h2 className="text-lg font-bold text-[var(--cb-ink)]">How can we help?</h2>
              <p className="text-[var(--cb-ink-secondary)] text-base leading-relaxed">
                Ask about stores, food, movies, or hours — instantly. Sign in only when you book.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-2">
              {[
                { icon: Store, label: "Stores" },
                { icon: Utensils, label: "Food" },
                { icon: Film, label: "Movies" },
                { icon: Clock, label: "Hours" },
              ].map((item) => (
                <div key={item.label} className="flex items-center gap-2 p-2.5 rounded-lg bg-gray-50 text-[var(--cb-ink-secondary)]">
                  <item.icon className="h-5 w-5 text-[#EC1A23]" aria-hidden="true" />
                  <span className="text-base font-semibold">{item.label}</span>
                </div>
              ))}
            </div>

            <button
              type="button"
              onClick={handleAcceptTerms}
              className="w-full py-3.5 min-h-12 bg-gradient-to-r from-[#EC1A23] to-[#561D1A] text-white text-base font-semibold rounded-lg flex items-center justify-center gap-2"
              data-testid="accept-terms-btn"
            >
              <CheckCircle className="h-5 w-5" aria-hidden="true" />
              Start chatting
            </button>

            <button
              type="button"
              onClick={openVisitorLogin}
              className="w-full py-2 text-base text-[#EC1A23] font-semibold"
              data-testid="welcome-sign-in-btn"
            >
              Already registered? Sign in
            </button>

            <p className="text-base text-[var(--cb-ink-muted)] text-center leading-relaxed pt-1">
              By continuing you agree to our{" "}
              <a href={POLICY_LINKS.terms} target="_blank" rel="noopener noreferrer" className="underline text-[#EC1A23]">Terms</a>
              {", "}
              <a href={POLICY_LINKS.privacy} target="_blank" rel="noopener noreferrer" className="underline text-[#EC1A23]">Privacy</a>
              {" & "}
              <a href={POLICY_LINKS.cookies} target="_blank" rel="noopener noreferrer" className="underline text-[#EC1A23]">Cookies</a>
              .
            </p>
          </div>
        </div>
      </div>
      {idleOverlay}
      </>
    );
  }

  return (
    <>
    <div className={embedMode ? "chatbot-app cb-safe-shell h-full min-h-0 w-full bg-white" : "chatbot-app min-h-screen bg-[var(--cb-surface)] flex items-center justify-center p-2 sm:p-4"} data-testid="chatbot-main">
      <div className={embedMode ? "relative w-full h-full min-h-0 bg-white flex flex-col overflow-hidden" : "relative w-full max-w-md h-[calc(100vh-16px)] sm:h-[700px] bg-white shadow-xl rounded-xl flex flex-col overflow-hidden"}>
        {/* Header — logo is the brand mark; no duplicate chat avatar on home */}
        <div className="bg-gradient-to-r from-[#EC1A23] to-[#561D1A] px-3 sm:px-4 py-2.5 text-white flex items-center gap-2 sm:gap-3">
          {activeView !== "chat" && (
            <button onClick={handleSmartBack} className="p-2 -ml-1 sm:-ml-2 hover:bg-white/10 rounded-full shrink-0" data-testid="back-btn" aria-label="Go back">
              <ArrowLeft className="h-5 w-5" />
            </button>
          )}
          {activeView !== "chat" ? (
            <div className="w-8 h-8 bg-white/20 flex items-center justify-center rounded-full shrink-0" aria-hidden="true">
              {activeView === "stores" || activeView === "storeDetail" ? <Store className="h-4 w-4" /> :
               activeView === "services" || activeView === "serviceDetail" ? <BriefcaseBusiness className="h-4 w-4" /> :
               activeView === "restaurants" || activeView === "restaurantDetail" || activeView === "tableBooking" ? <Utensils className="h-4 w-4" /> :
               activeView.includes("movie") || activeView === "seatSelection" ? <Film className="h-4 w-4" /> :
               <MessageSquare className="h-4 w-4" />}
            </div>
          ) : null}
          <div className="flex-1 min-w-0">
            <h1 className="cb-page-title text-[0.95rem] sm:text-[1rem] leading-tight truncate">
              {activeView === "stores" ? "Mall Stores" :
               activeView === "storeDetail" ? selectedStore?.name :
               activeView === "services" ? "Services" :
               activeView === "serviceDetail" ? selectedService?.name :
               activeView === "restaurants" ? "Restaurants" :
               activeView === "restaurantDetail" ? selectedRestaurant?.name :
               activeView === "tableBooking" ? "Book a Table" :
               activeView === "movies" ? "Now Showing" :
               activeView === "movieSessions" ? selectedMovie?.title :
               activeView === "seatSelection" ? "Select Seats" :
               "Customer Support"}
            </h1>
            <p className="text-xs text-white/80 font-medium">
              {activeView === "chat" ? (
                <span className="flex items-center gap-1.5">
                  <span className={`w-2 h-2 rounded-full ${isOnline ? "bg-green-400" : "bg-gray-400"}`} aria-hidden="true" />
                  <span className="sr-only">{isOnline ? "Online" : "Offline"}</span>
                  <span aria-hidden="true">{isOnline ? "Online" : "Offline"}</span>
                </span>
              ) : activeView === "seatSelection" && selectedSession ? 
                `${formatSessionDate(selectedSession.date)} • ${selectedSession.time}` :
                activeView === "storeDetail" || activeView === "serviceDetail" || activeView === "restaurantDetail" ?
                "Contact details" :
                "Tap for details"}
            </p>
          </div>
          <img
            src={`${process.env.PUBLIC_URL || ""}/westgate-logo-white.png`}
            alt="Westgate Shopping Mall"
            className="h-8 sm:h-9 w-auto max-w-[38%] object-contain object-right shrink-0"
            style={{
              filter: "brightness(1.15) contrast(1.2)",
              WebkitFilter: "brightness(1.15) contrast(1.2)",
              imageRendering: "auto",
            }}
            data-testid="header-westgate-logo"
          />
          {embedMode && (
            <button
              type="button"
              onClick={minimiseWidget}
              className="p-2 -mr-1 hover:bg-white/10 rounded-full shrink-0"
              aria-label="Minimise assistant"
              data-testid="minimise-btn"
            >
              <ChevronDown className="h-5 w-5" />
            </button>
          )}
        </div>

        {cinemaBookingStepId ? <BookingStepper currentStepId={cinemaBookingStepId} /> : null}

        {/* Content Area */}
        <div className="flex-1 overflow-hidden flex flex-col bg-gray-50">
          {activeView === "chat" && (
            <>
              <div className="flex-1 overflow-y-auto px-3 py-2" data-testid="messages-container">
                {messages.map((msg, index) => {
                  const prevMsg = messages[index - 1];
                  const nextMsg = messages[index + 1];
                  const showDateSeparator = isDifferentDay(prevMsg, msg);
                  const isLastInGroup = !nextMsg || nextMsg.role !== msg.role || isDifferentDay(msg, nextMsg);
                  
                  return (
                    <div key={msg.id}>
                      {showDateSeparator && <DateSeparator date={formatDate(msg.timestamp)} />}
                      <MessageBubble
                        message={msg}
                        isUser={msg.role === "user"}
                        showTail={isLastInGroup}
                        isRead={msg.role === "user"}
                        onSelectMovie={handleSelectMovie}
                      />
                    </div>
                  );
                })}
                {loading && <TypingIndicator />}
                <div ref={messagesEndRef} />
              </div>

              <div className="px-3 py-2 bg-white border-t">
                <div className="flex gap-2 overflow-x-auto pb-1" aria-label="Quick actions">
                  {QUICK_ACTIONS.map((action) => (
                    <button
                      key={action.id}
                      type="button"
                      onClick={() => handleQuickAction(action.id)}
                      className="flex items-center gap-2 min-h-11 px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg hover:border-[#EC1A23] hover:bg-red-50 transition-colors whitespace-nowrap"
                      data-testid={`quick-action-${action.id}`}
                    >
                      <action.icon className="h-4 w-4 text-[#EC1A23]" aria-hidden="true" />
                      <span className="text-sm text-[var(--cb-ink-secondary)] font-medium">{action.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <form onSubmit={handleSendMessage} className="px-3 py-2 bg-white flex items-end gap-2 relative">
                <div className="flex-1 flex items-center bg-gray-100 rounded-full relative pl-4 pr-3 min-h-12">
                  <SmartSuggestions suggestions={suggestions} onSelect={handleSuggestionSelect} loading={suggestionsLoading && inputValue.length >= 2} />
                  <input
                    ref={inputRef}
                    type="text"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onFocus={() => setInputPlaceholder((current) => pickSampleQuestion(current))}
                    placeholder={inputPlaceholder}
                    aria-label="Ask about the mall"
                    className="flex-1 py-3 text-sm focus:outline-none bg-transparent text-[var(--cb-ink)] placeholder:text-[var(--cb-ink-muted)]"
                    data-testid="chat-input"
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading || !inputValue.trim()}
                  className="w-12 h-12 shrink-0 flex items-center justify-center bg-gradient-to-r from-[#EC1A23] to-[#d64548] text-white rounded-full disabled:opacity-50"
                  aria-label="Send message"
                  data-testid="send-btn"
                >
                  <Send className="h-5 w-5" aria-hidden="true" />
                </button>
              </form>
            </>
          )}

          {activeView === "stores" && (
            <div className="flex-1 overflow-y-auto bg-white">
              <div className="p-3 border-b sticky top-0 bg-white z-10">
                <input
                  type="text"
                  placeholder="Search stores..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full px-4 py-3 min-h-11 bg-gray-100 rounded-lg focus:outline-none text-sm"
                  aria-label="Search stores"
                />
                <DirectoryFloorChips
                  items={stores}
                  selectedFloor={directoryFloor}
                  onSelect={setDirectoryFloor}
                />
              </div>
              <div className="divide-y">
                {filteredStores.length === 0 ? (
                  <div className="p-8 text-center text-[var(--cb-ink-muted)]">
                    <Store className="h-12 w-12 mx-auto mb-3 opacity-30" />
                    <p>No stores found</p>
                  </div>
                ) : filteredStores.map((store) => (
                  <button
                    type="button"
                    key={store.id} 
                    className="w-full text-left p-4 hover:bg-gray-50 cursor-pointer active:bg-gray-100 transition-colors"
                    onClick={() => handleSelectStore(store)}
                  >
                    <div className="flex items-start gap-3">
                      <div className="relative w-12 h-12">
                        {directoryLogoUrl(store) && (
                          <img 
                            src={directoryLogoUrl(store)} 
                            alt=""
                            className="w-12 h-12 rounded-lg object-cover bg-gray-100 absolute inset-0"
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        )}
                        <div className="w-12 h-12 bg-gradient-to-br from-[#EC1A23] to-[#561D1A] flex items-center justify-center text-white font-bold rounded-lg">
                          {store.name?.charAt(0)}
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-sm truncate text-[var(--cb-ink)]">{store.name}</h3>
                        <p className="text-xs text-[#EC1A23] capitalize font-medium">{store.category}</p>
                        <div className="flex items-center gap-1 mt-1 text-xs text-[var(--cb-ink-muted)]">
                          <MapPin className="h-3 w-3" aria-hidden="true" />
                          <span>
                            {normalizeFloorLabel(store.floor)}
                            {store.unit_number ? `, Unit ${store.unit_number}` : ""}
                          </span>
                        </div>
                      </div>
                      <ChevronRight className="h-5 w-5 text-gray-300" aria-hidden="true" />
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {activeView === "storeDetail" && selectedStore && (
            <ItemDetailView item={selectedStore} type="store" onBack={() => setActiveView("stores")} />
          )}

          {activeView === "services" && (
            <div className="flex-1 overflow-y-auto bg-white">
              <div className="p-3 border-b sticky top-0 bg-white z-10">
                <input
                  type="text"
                  placeholder="Search services..."
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  className="w-full px-4 py-3 min-h-11 bg-gray-100 rounded-lg focus:outline-none text-sm"
                  aria-label="Search services"
                />
                <DirectoryFloorChips
                  items={services}
                  selectedFloor={directoryFloor}
                  onSelect={setDirectoryFloor}
                />
              </div>
              <div className="divide-y">
                {filteredServices.length === 0 ? (
                  <div className="p-8 text-center text-[var(--cb-ink-muted)]">
                    <BriefcaseBusiness className="h-12 w-12 mx-auto mb-3 opacity-30" />
                    <p>No services found</p>
                  </div>
                ) : filteredServices.map((service) => (
                  <button
                    type="button"
                    key={service.id}
                    className="w-full text-left p-4 hover:bg-gray-50 cursor-pointer active:bg-gray-100 transition-colors"
                    onClick={() => handleSelectService(service)}
                  >
                    <div className="flex items-start gap-3">
                      <div className="relative w-12 h-12">
                        {directoryLogoUrl(service) && (
                          <img
                            src={directoryLogoUrl(service)}
                            alt=""
                            className="w-12 h-12 rounded-lg object-cover bg-gray-100 absolute inset-0"
                            onError={(event) => {
                              event.target.style.display = 'none';
                            }}
                          />
                        )}
                        <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center text-white rounded-lg">
                          <BriefcaseBusiness className="h-6 w-6" aria-hidden="true" />
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-sm truncate text-[var(--cb-ink)]">{service.name}</h3>
                        <p className="text-xs text-amber-700 font-medium">Service</p>
                        <div className="flex items-center gap-1 mt-1 text-xs text-[var(--cb-ink-muted)]">
                          <MapPin className="h-3 w-3" aria-hidden="true" />
                          <span>
                            {normalizeFloorLabel(service.floor)}
                            {service.unit_number ? `, Unit ${service.unit_number}` : ""}
                          </span>
                        </div>
                      </div>
                      <ChevronRight className="h-5 w-5 text-gray-300" aria-hidden="true" />
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {activeView === "serviceDetail" && selectedService && (
            <ItemDetailView item={selectedService} type="service" onBack={() => setActiveView("services")} />
          )}

          {activeView === "restaurants" && (
            <div className="flex-1 overflow-y-auto bg-white">
              <div className="p-3 border-b sticky top-0 bg-white z-10">
                <input
                  type="text"
                  placeholder="Search restaurants..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full px-4 py-3 min-h-11 bg-gray-100 rounded-lg focus:outline-none text-sm"
                  aria-label="Search restaurants"
                />
                <DirectoryFloorChips
                  items={restaurants}
                  selectedFloor={directoryFloor}
                  onSelect={setDirectoryFloor}
                />
              </div>
              <div className="divide-y">
                {filteredRestaurants.length === 0 ? (
                  <div className="p-8 text-center text-[var(--cb-ink-muted)]">
                    <Utensils className="h-12 w-12 mx-auto mb-3 opacity-30" />
                    <p>No restaurants found</p>
                  </div>
                ) : filteredRestaurants.map((restaurant) => (
                  <button
                    type="button"
                    key={restaurant.id} 
                    className="w-full text-left p-4 hover:bg-gray-50 cursor-pointer active:bg-gray-100 transition-colors"
                    onClick={() => handleSelectRestaurant(restaurant)}
                  >
                    <div className="flex items-start gap-3">
                      <div className="relative w-12 h-12">
                        {directoryLogoUrl(restaurant) && (
                          <img 
                            src={directoryLogoUrl(restaurant)} 
                            alt=""
                            className="w-12 h-12 rounded-lg object-cover bg-gray-100 absolute inset-0"
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        )}
                        <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center text-white rounded-lg">
                          <Coffee className="h-6 w-6" aria-hidden="true" />
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-sm truncate text-[var(--cb-ink)]">{restaurant.name}</h3>
                        <p className="text-xs text-emerald-700 font-medium">{restaurant.cuisine_type}</p>
                        <div className="flex items-center gap-3 mt-1 text-xs text-[var(--cb-ink-muted)]">
                          <span className="flex items-center gap-1">
                            <MapPin className="h-3 w-3" aria-hidden="true" />
                            {normalizeFloorLabel(restaurant.floor)}
                          </span>
                        </div>
                      </div>
                      <ChevronRight className="h-5 w-5 text-gray-300" aria-hidden="true" />
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {activeView === "restaurantDetail" && selectedRestaurant && (
            <ItemDetailView
              item={selectedRestaurant}
              type="restaurant"
              onBack={() => setActiveView("restaurants")}
              onBookTable={handleBookTable}
            />
          )}

          {activeView === "tableBooking" && selectedRestaurant && (
            <TableBookingPanel
              restaurant={selectedRestaurant}
              visitor={visitorData}
              visitorAuthenticated={visitorAuthenticated}
              onRequireAuth={() => {
                setPendingAuthAction("tableBooking");
                openVisitorLogin();
              }}
              onBack={() => setActiveView("restaurantDetail")}
              onSubmitted={(result) => {
                addBotMessage(
                  `Your table request at ${selectedRestaurant.name} is pending confirmation.\nRef: ${result.reference_id}\nWe'll notify you by WhatsApp and email once it's confirmed.`
                );
              }}
            />
          )}

          {activeView === "movies" && (
            <div className="flex-1 overflow-y-auto bg-[var(--cb-surface)]">
              <div className="p-3 space-y-3">
                {moviesLoading && movies.length === 0 ? (
                  <div className="p-8 text-center text-[var(--cb-ink-muted)]">
                    <Loader2 className="h-8 w-8 mx-auto mb-3 animate-spin text-[var(--cb-brand)]" />
                    <p className="cb-meta">Loading movies…</p>
                  </div>
                ) : movies.length === 0 ? (
                  <div className="p-8 text-center text-[var(--cb-ink-muted)]">
                    <Film className="h-12 w-12 mx-auto mb-3 opacity-30" />
                    <p className="cb-meta">No movies showing</p>
                  </div>
                ) : [...movies].sort(compareByTitleAsc).map((movie) => (
                  <div 
                    key={movie.id} 
                    onClick={() => handleSelectMovie(movie)}
                    className="bg-[var(--cb-card)] border border-[var(--cb-chip-border)] rounded-xl overflow-hidden hover:border-[var(--cb-brand)] transition-all cursor-pointer"
                    data-testid={`movie-card-${movie.id}`}
                  >
                    <div className="p-3.5 flex gap-3.5">
                      <div className="w-[4.75rem] h-[6.75rem] bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                        {movie.poster_url ? (
                          <img 
                            src={movie.poster_url} 
                            alt={movie.title}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-[#EC1A23]/15 to-[#561D1A]/20 flex items-center justify-center">
                            <Film className="h-8 w-8 text-[var(--cb-brand)]" />
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="cb-headline line-clamp-2">{movie.title}</h3>
                        <div className="flex items-center gap-2 mt-1.5">
                          {movie.rating && (
                            <span className="cb-rating">{movie.rating}</span>
                          )}
                          {movie.duration_minutes ? (
                            <span className="cb-meta">{movie.duration_minutes} min</span>
                          ) : null}
                        </div>
                        {movie.genre && (
                          <p className="cb-genre mt-1.5">{movie.genre}</p>
                        )}
                        <div className="flex flex-wrap gap-1.5 mt-2.5">
                          {movie.showtimes?.slice(0, 4).map((time, i) => {
                            const parsed = parseShowtimeLabel(time);
                            return (
                              <span key={i} className="cb-showtime-chip">
                                <span className="cb-showtime-time">{parsed.time}</span>
                                {parsed.screen ? (
                                  <span className="cb-showtime-screen">{parsed.screen}</span>
                                ) : null}
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeView === "movieSessions" && selectedMovie && (
            <div className="flex-1 overflow-y-auto bg-[var(--cb-surface)]">
              {/* Movie Info */}
              <div className="p-4 border-b bg-[var(--cb-card)]">
                <div className="flex gap-3">
                  <div className="w-16 h-24 rounded-lg overflow-hidden bg-gradient-to-br from-[#EC1A23]/15 to-[#561D1A]/20 flex items-center justify-center shrink-0">
                    {selectedMovie.poster_url ? (
                      <img
                        src={selectedMovie.poster_url}
                        alt={selectedMovie.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <Film className="h-8 w-8 text-[var(--cb-brand)]" />
                    )}
                  </div>
                  <div className="min-w-0">
                    <h2 className="cb-headline-lg">{selectedMovie.title}</h2>
                    <div className="flex items-center gap-2 mt-1.5">
                      {selectedMovie.rating && (
                        <span className="cb-rating">{selectedMovie.rating}</span>
                      )}
                      {selectedMovie.duration_minutes ? (
                        <span className="cb-meta">{selectedMovie.duration_minutes} min</span>
                      ) : null}
                    </div>
                    {selectedMovie.genre && (
                      <p className="cb-genre mt-1.5">{selectedMovie.genre}</p>
                    )}
                  </div>
                </div>
              </div>

              <div className="p-4 space-y-4">
                {/* Date chips — matches cinema booking date selector */}
                <div className="flex gap-2 overflow-x-auto pb-1">
                  {sortedDates.map((date) => {
                    const chip = formatDateChip(date);
                    const isActive = date === selectedShowDate;
                    return (
                      <button
                        key={date}
                        type="button"
                        onClick={() => setSelectedShowDate(date)}
                        className={`min-w-[88px] shrink-0 rounded-xl px-3 py-2.5 text-left border transition-all ${
                          isActive
                            ? 'bg-[var(--cb-brand)] border-[var(--cb-brand)] text-white shadow-sm'
                            : 'bg-[var(--cb-card)] border-[var(--cb-chip-border)] text-[var(--cb-ink-secondary)] hover:border-[var(--cb-brand)]/40'
                        }`}
                        data-testid={`show-date-${date}`}
                      >
                        <p className={`text-sm font-bold leading-tight ${isActive ? 'text-white' : 'text-[var(--cb-ink)]'}`}>
                          {chip.primary}
                        </p>
                        <p className={`text-xs mt-0.5 font-medium ${isActive ? 'text-white/90' : 'text-[var(--cb-ink-muted)]'}`}>
                          {chip.secondary}
                        </p>
                      </button>
                    );
                  })}
                </div>

                <h3 className="cb-section-title">Showtimes</h3>
                {sessionsSource === "catalog" && !sessionsLoading && (
                  <p className="text-xs text-[var(--cb-ink-muted)] -mt-2">
                    Latest synced times. Seat booking resumes when the cinema timetable reconnects.
                  </p>
                )}

                <div className={`grid gap-3 ${sessionsForSelectedDate.length > 1 ? 'grid-cols-2' : 'grid-cols-1'}`}>
                  {sessionsForSelectedDate.map((session) => {
                    const isVip = Boolean(session.is_vip) || /vip/i.test(session.screen || '');
                    const is3D = Boolean(session.is_3d);
                    const canBook = session.bookable !== false;
                    return (
                      <button
                        key={session.id}
                        type="button"
                        onClick={() => handleSelectSession(session)}
                        disabled={!canBook}
                        className={`w-full text-left rounded-2xl border border-[var(--cb-chip-border)] bg-[var(--cb-card)] p-3 sm:p-4 transition-all ${
                          canBook ? 'hover:border-[var(--cb-brand)] hover:shadow-sm' : 'opacity-90 cursor-default'
                        }`}
                        data-testid={`session-${session.id}`}
                      >
                        <div className="flex flex-wrap gap-1.5 mb-2.5">
                          {selectedMovie.genre && (
                            <span className="inline-flex px-2 py-0.5 rounded-md text-[10px] font-bold tracking-wide uppercase bg-[var(--cb-chip)] text-[var(--cb-ink-secondary)]">
                              {String(selectedMovie.genre)}
                            </span>
                          )}
                          {isVip && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold tracking-wide uppercase bg-amber-100 text-amber-900">
                              <Crown className="h-3 w-3" />
                              VIP
                            </span>
                          )}
                          {is3D ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold tracking-wide uppercase bg-[#EC1A23]/10 text-[var(--cb-brand)] border border-[#EC1A23]/20">
                              <Glasses className="h-3.5 w-3.5 glasses-3d-bounce" />
                              3D
                            </span>
                          ) : (
                            <span className="inline-flex px-2 py-0.5 rounded-md text-[10px] font-bold tracking-wide uppercase bg-[var(--cb-chip)] text-[var(--cb-ink-secondary)]">
                              2D
                            </span>
                          )}
                        </div>
                        <p className="cb-session-time">
                          {session.time.replace(/^0/, '')}
                        </p>
                        <p className={`mt-1.5 text-xs font-semibold flex items-center gap-1 ${isVip ? 'text-amber-800' : 'text-[var(--cb-ink-muted)]'}`}>
                          {isVip && <Crown className="h-3.5 w-3.5 shrink-0" />}
                          <span className="truncate">{session.screen}</span>
                        </p>
                        {is3D && (
                          <p className="mt-1.5 inline-flex items-center gap-1.5 text-[11px] font-semibold text-[var(--cb-ink-secondary)]">
                            <Glasses className="h-3.5 w-3.5 glasses-3d-pulse text-[var(--cb-brand)]" />
                            3D glasses at counter
                          </p>
                        )}
                        {session.price != null ? (
                          <p className="cb-price mt-2.5 text-sm sm:text-base">
                            KSh {Number(session.price).toLocaleString()}
                            <span className="text-[11px] font-semibold text-[var(--cb-brand)]/80"> / ticket</span>
                          </p>
                        ) : (
                          <p className="mt-2.5 text-xs font-medium text-[var(--cb-ink-muted)]">Time confirmed · price at booking</p>
                        )}
                      </button>
                    );
                  })}
                </div>

                {sessionsLoading && (
                  <div className="text-center py-8 text-gray-500">
                    <Loader2 className="h-8 w-8 mx-auto mb-3 animate-spin text-[var(--cb-brand)]" />
                    <p>Loading showtimes…</p>
                  </div>
                )}

                {!sessionsLoading && movieSessions.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    <Clock className="h-12 w-12 mx-auto mb-3 opacity-30" />
                    <p>No upcoming showtimes</p>
                  </div>
                )}

                {!sessionsLoading && movieSessions.length > 0 && sessionsForSelectedDate.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    <Clock className="h-12 w-12 mx-auto mb-3 opacity-30" />
                    <p>No showtimes for this date</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeView === "seatSelection" && selectedSession && (
            <div className="flex-1 overflow-y-auto bg-[#F7F0E8]">
              <div className="p-3">
                <SeatSelection 
                  seatPlan={seatPlan} 
                  selectedSeats={selectedSeats} 
                  onSeatSelect={handleSeatSelect}
                  is3D={selectedSession.is_3d}
                  isVip={Boolean(selectedSession.is_vip)}
                />
              </div>
              
              {/* Footer */}
              <div className="sticky bottom-0 p-4 bg-white border-t">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="text-sm font-medium text-[var(--cb-ink)]" aria-live="polite" aria-atomic="true">
                      {selectedSeats.length} seat{selectedSeats.length !== 1 ? 's' : ''} selected
                    </p>
                    {selectedSeats.length > 0 && (
                      <p className="text-xs text-[var(--cb-ink-muted)]">Seats: {selectedSeats.join(', ')}</p>
                    )}
                    <p className="text-xs text-[var(--cb-ink-muted)]">Max 10 seats · Locked for 7 mins at checkout</p>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-[#EC1A23]">
                      KES {((selectedSession.price || 0) * selectedSeats.length).toLocaleString()}
                    </p>
                    {selectedSession.is_3d && selectedSeats.length > 0 && (
                      <p className="text-xs text-amber-600">3D glasses sold at counter</p>
                    )}
                  </div>
                </div>
                {seatLockError && (
                  <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                    {seatLockError}
                  </div>
                )}
                <button
                  onClick={handleProceedToPayment}
                  disabled={selectedSeats.length === 0 || lockingSeats}
                  className="w-full py-3 bg-gradient-to-r from-[#EC1A23] to-[#d64548] text-white font-semibold rounded-lg disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="proceed-payment-btn"
                >
                  {lockingSeats ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" />
                      Locking seats...
                    </>
                  ) : (
                    <>
                      <CreditCard className="h-5 w-5" />
                      Lock Seats & Continue
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Booking steps fill this same window instead of floating over it */}
        {showPaymentModal && selectedMovie && selectedSession && seatReservation && (
          <PaymentModal
            movie={selectedMovie}
            session={selectedSession}
            seats={selectedSeats}
            seatPlan={seatPlan}
            reservationId={seatReservation.id}
            expiresAt={seatReservation.expires_at}
            visitor={visitorData}
            onClose={handleClosePaymentModal}
            onPaymentComplete={handlePaymentComplete}
            onHoldExpired={handleHoldExpired}
            onAwaitingMpesa={handleAwaitingMpesa}
          />
        )}

        {showMarketingPrefs && (
          <div
            className="absolute inset-0 z-40 flex items-end sm:items-center justify-center bg-[var(--cb-overlay)] p-3"
            role="dialog"
            aria-modal="true"
            aria-labelledby="marketing-prefs-title"
            data-testid="marketing-prefs-sheet"
          >
            <div className="w-full max-w-sm bg-white rounded-xl shadow-xl overflow-hidden">
              <div className="bg-gradient-to-r from-[#EC1A23] to-[#561D1A] p-4 text-white">
                <h2 id="marketing-prefs-title" className="cb-page-title text-xl">Get mall updates?</h2>
                <p className="text-white/85 text-base mt-1">Optional — skip anytime. Confirm later by email.</p>
              </div>
              <div className="p-4 space-y-3">
                <p className="text-base text-[var(--cb-ink-secondary)] leading-relaxed">
                  Choose channels for deals, offers, and Westgate news. We’ll email an opt-in link first.
                </p>
                {[
                  { key: "email", label: "Email" },
                  { key: "sms", label: "SMS" },
                  { key: "whatsapp", label: "WhatsApp" },
                ].map((channel) => (
                  <label key={channel.key} className="flex items-center gap-3 min-h-11 px-2 rounded-lg bg-gray-50">
                    <input
                      type="checkbox"
                      checked={marketingConsent[channel.key]}
                      onChange={(event) =>
                        setMarketingConsent((prev) => ({
                          ...prev,
                          [channel.key]: event.target.checked,
                        }))
                      }
                      className="h-4 w-4 accent-[#EC1A23]"
                    />
                    <span className="text-base font-medium text-[var(--cb-ink)]">{channel.label}</span>
                  </label>
                ))}
                <button
                  type="button"
                  disabled={marketingPrefsLoading || !(marketingConsent.email || marketingConsent.sms || marketingConsent.whatsapp)}
                  onClick={() => submitMarketingPreferences(false)}
                  className="w-full min-h-11 py-3 rounded-lg bg-[#EC1A23] text-white text-base font-semibold disabled:opacity-50"
                  data-testid="marketing-prefs-save"
                >
                  {marketingPrefsLoading ? "Saving…" : "Send me the opt-in email"}
                </button>
                <button
                  type="button"
                  disabled={marketingPrefsLoading}
                  onClick={() => submitMarketingPreferences(true)}
                  className="w-full min-h-11 py-2 text-base font-semibold text-[var(--cb-ink-muted)]"
                  data-testid="marketing-prefs-skip"
                >
                  Skip for now
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
    {idleOverlay}
    </>
  );
}
